// Prequalification funnel — BUYER track. Anonymous multi-step flow with a
// live sizing meter (client-side indicative math — see prequalSizing.ts;
// the server engine stays authoritative at finalize) and a per-step AI
// guide (usePrequalGuide: entry-gated on the step's primary fields,
// debounced auto-refresh on answer changes, manual "Update tip"). State
// persists to sessionStorage (§7 — kept post-finalize for the Result
// route; cleared only by "Start over"); finalize is the LAST FORM STEP and
// success navigates to /portal/prequalify/buyer/result. Chrome + layout
// follow the §4 public-funnel design system.

import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormField, Input, Button, Textarea } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import { PortalShell } from "../../components/PortalShell";
import {
  prequalificationApi,
  type PrequalBuyerAnswers,
  type PrequalBuyerDocumentPath,
  type PrequalBuyerType,
  type PrequalEquitySource,
  type PrequalInternalRelationship,
  type PrequalOperatorArrangement,
  type PrequalCashEquivalents,
  type PrequalFinalizeResult,
} from "../../api/prequalification";
import {
  computeBuyerSizing,
  isCompanyTypeAnswer,
  statedCashAtClosing,
  totalDownPaymentCapacity,
} from "./prequalSizing";
import {
  BUYER_KNOCKOUTS,
  DOCUMENT_PATH_LETTER_ONLY,
  DOCUMENT_PATH_CARDS,
  DOCUMENT_PATH_WITH_DEAL_SHEET,
  DOCUMENT_PATH_INTERNAL,
  INTERNAL_RELATIONSHIP_OPTIONS,
  INTERNAL_RELATIONSHIP_WITH_TENURE,
  GEOGRAPHY_OPTIONS,
  HOME_CITY_HELPER,
  HOME_CITY_LABEL,
  HOME_CITY_PLACEHOLDER,
  INDUSTRY_HELPER_DEAL,
  INDUSTRY_HELPER_INTERNAL,
  INDUSTRY_HELPER_LETTER_ONLY,
  INDUSTRY_LADDER,
  INDUSTRY_PLACEHOLDER,
  INDUSTRY_QUESTION,
  INVOLVEMENT_OPTIONS,
  MANAGEMENT_LADDER,
  OWNERSHIP_LADDER,
  PRICE_BANDS,
  ASSIST_SAVE_NOTE,
  FUNNEL_MARKETING_FALLBACK,
  FUNNEL_QUESTION_DEFAULTS,
  priceBandForAmount,
  PROGRAM_TERMS_SUBHEAD,
  RESERVES_BANDS,
  REVIEW_CASH_ASSUMPTION_NONE,
  REVIEW_CASH_ASSUMPTION_PREFIX,
  REVIEW_CASH_ASSUMPTION_SUFFIX,
  REVIEW_DEAL_RECAP_NOTE_INTERNAL,
  REVIEW_DEAL_RECAP_TITLE,
  STEP_TITLE_BUYER_START,
  type FunnelQuestionKey,
} from "./prequalContent";
import type { PrequalFunnelMarketingCopy } from "../../api/prequalFunnelCopy";
import {
  blockSlug,
  ChoiceField,
  CurrencyField,
  FinalizeSection,
  formatMoneyTyping,
  FunnelProgress,
  GuidePanel,
  KnockoutChecklist,
  nullIfNotPositive,
  optionsFromCopy,
  parseMoney,
  ProgramGlanceCard,
  QuestionCard,
  questionBlock,
  SizingMeter,
  type FunnelStep,
  type QuestionCopy,
} from "./PrequalBits";
import { usePrequalGuide } from "./usePrequalGuide";
import { usePrequalInvite } from "./usePrequalInvite";
import { usePrequalBuyLandingToken } from "./usePrequalBuyLandingToken";
import { useBuyLandingPrefill } from "./useBuyLandingPrefill";
import { usePrequalAdToken } from "./usePrequalAdToken";
import { PrequalInviteExpiredCard } from "./PrequalInviteExpiredCard";
import {
  useFunnelCopy,
  usePartnerBrand,
  usePrequalBase,
  usePrequalPartnerSlug,
} from "./usePartnerBrand";
import { PartnerFallbackNotice, PartnerThemeProvider } from "./PartnerThemeProvider";
import { hasText, textOrUndefined } from "./blankCopy";
import { PrequalSaveProgressCard } from "./PrequalSaveProgressCard";
import type { PrequalDraftLoad, PrequalFunnelPageProps } from "./prequalDraftAdapter";
import { useAnonymousPrequalDraftAdapter } from "./useAnonymousPrequalDraftAdapter";
import { usePrequalDraftSession } from "./usePrequalDraftSession";
import {
  clearFunnelState,
  loadScopedFunnelState,
  PREQUAL_BUYER_STORAGE_KEY,
  saveFunnelState,
  type PrequalSessionScope,
} from "./prequalStorage";
import { usePrequalAttributionCapture } from "./prequalAttribution";

/**
 * The buyer funnel’s six steps.
 *
 * 🔴 SIX BEFORE AND SIX AFTER, AND THAT IS A CONSTRAINT RATHER THAN A
 * COINCIDENCE: `PrequalificationFunnelSteps` carries the count server-side as the
 * denominator of the draft’s progress, and `prequalSessionScope.test.tsx` pins the two
 * against each other. A seventh step is a change on the C# side as well as here.
 *
 * What moved, and why:
 *
 *  - **start** is new, and it holds the package fork ALONE. The fork used to sit
 *    at the top of a step carrying seven other fields while being silently
 *    required, so a buyer filled all of them, pressed Continue, and only then met
 *    "Choose what you need from Phoenix to continue" at the foot of the page —
 *    a complaint about the first control, rendered below the last. Its own step
 *    makes that unreachable, and it is the honest place for it: every question
 *    after it differs by the answer.
 *  - **target** keeps its name and loses the fork. It branches three ways.
 *  - **capital** is unchanged.
 *  - **background** is the old "experience" plus the two OPERATOR questions that
 *    were stranded on the fit check — how the buyer will be involved, and where
 *    they will operate. Neither is an eligibility question; both describe the
 *    person, which is what this step is.
 *  - **eligibility** is the fit check with those two removed, so it is now the
 *    knockouts and nothing else — one kind of question per step.
 *  - **review** is the old finalize plus goals. One optional text box never
 *    justified a sixth of the progress bar, and a buyer who skipped it still had
 *    to press Continue past it.
 *
 * ⚠️ THE LABELS ARE ONE WORD EACH, and "Background" rather than "Your
 * background" for a reason that is not taste: these render as BUTTONS in the
 * stepper, so their text is their accessible name, and "Your background"
 * contains "back" — which made `getByRole("button", { name: /back/i })` match
 * the pill as well as the funnel’s own ← Back control. The possessive bought
 * nothing the heading underneath does not already say, and cost an ambiguity in
 * every name-based query on every step. The chip LABELS are navigation
 * furniture sized to a chip; the headings live on {@link buyerStepMeta}.
 */
export const BUYER_STEPS: FunnelStep[] = [
  { key: "start", label: "Start" },
  { key: "target", label: "Target" },
  { key: "capital", label: "Capital" },
  { key: "background", label: "Background" },
  { key: "eligibility", label: "Eligibility" },
  { key: "review", label: "Review" },
];

/**
 * Each step's heading and hint, built from the funnel's resolved marketing copy.
 *
 * A FUNCTION rather than a constant because these words are now staff-editable
 * without a deploy (document key `funnel-copy`): the caller passes what
 * `useFunnelCopy()` resolved — a published override, else the compiled-in default.
 * FIVE of the six are; `start` is not, and the arm below says why rather than
 * leaving a reader to infer that somebody forgot it.
 *
 * `stepTitleBuyerGoals` is deliberately no longer read HERE. Goals is not a step
 * any more — it is a field on the review step — and the key is still resolved and
 * still rendered, as that field’s label. Dropping the key instead would break the
 * cross-language parity fixture; leaving it resolved and unrendered would orphan
 * a sentence an admin can still publish.
 * The stepper CHIP labels are deliberately not included; they live on
 * {@link BUYER_STEPS} and are navigation furniture sized to a chip, not headings.
 *
 * The CAPITAL step's two strings name CAPITAL rather than cash, because the step
 * does: the engine divides cash plus every named non-cash asset, so a heading
 * asking only about cash tells a buyer with none that this step is not for them —
 * directly above the panel that would have sized them. That wording arrived in
 * #1064 and now lives in the copy store's defaults (and its parity fixture)
 * rather than here, so an admin edit and a code default cannot disagree.
 */
export function buyerStepMeta(
  m: PrequalFunnelMarketingCopy,
  companyBuyer = false,
): Record<string, { title: string; hint?: string }> {
  return {
    // ⚠️ THE ONE HARD-CODED TITLE. There is no `stepTitleBuyerStart` to resolve:
    // the marketing keys are pinned against a JSON fixture shared with the .NET
    // test project, so minting a seventh is a change on the C# side, in that
    // fixture and in the copy store’s defaults — none of which belong to a
    // funnel restructure. See STEP_TITLE_BUYER_START’s own note.
    start: { title: STEP_TITLE_BUYER_START },
    target: { title: m.stepTitleBuyerTarget },
    // A company buyer is never asked for other assets (Q3, M1), so its hint
    // names only the equity contribution.
    capital: {
      title: m.stepTitleBuyerCapital,
      hint: companyBuyer ? m.stepHintBuyerCapitalCompany : m.stepHintBuyerCapital,
    },
    background: { title: m.stepTitleBuyerExperience },
    eligibility: { title: m.stepTitleBuyerFitCheck },
    review: { title: m.stepTitleBuyerFinalize, hint: m.stepHintBuyerFinalize },
  };
}

/** The SHIPPED DEFAULTS — the same map this page rendered before the copy became
 *  editable. Exported because `DealExplorerPage.test.tsx` asserts the funnel's
 *  own vocabulary against it, and the shipped default is what that test is about:
 *  an admin's later edit is theirs to get right, but what Phoenix ships must be
 *  clean of the forbidden terms. */
export const BUYER_STEP_META = buyerStepMeta(FUNNEL_MARKETING_FALLBACK);

/**
 * The five non-cash down-payment categories, in the order they render — which
 * is the order the wire type declares them.
 *
 * The labels are BORROWER-FACING Document Language blocks
 * (`option-buyer-other-asset-<key>`, see {@link otherAssetBlock}) and the wire
 * name ("cash equivalents") never appears on screen. The set is CLOSED on both
 * sides of the wire, so a sixth category is a deliberate change here, in
 * `PrequalCashEquivalents`, and in the haircut tables in both languages — not a
 * line added to this array. The staff call reads this same list.
 */
export const OTHER_ASSET_CATEGORIES = [
  "realEstateEquity",
  "securities",
  "businessEquity",
  "retirementAccounts",
  "otherAssets",
] as const;

export type OtherAssetKey = (typeof OTHER_ASSET_CATEGORIES)[number];

/** One category's block key: `realEstateEquity` → `option-buyer-other-asset-real-estate-equity`. */
function otherAssetBlock(key: OtherAssetKey): FunnelQuestionKey {
  return questionBlock(`option-buyer-other-asset-${blockSlug(key)}`);
}

/**
 * What the capital step says when the buyer has named no capital AT ALL.
 *
 * TOTAL capital, never cash: the sizing engine has divided the COMBINED
 * capacity — cash plus every named non-cash asset — since that section shipped,
 * so a buyer with $0 in the bank and $100,000 of business equity is fully
 * sizeable and must not be stopped here. The old copy ("Enter the cash you have
 * available") described a rule the engine had already stopped applying.
 */
const NO_CAPITAL_ERROR =
  "Add the cash or other assets you can put toward the down payment — we need at least one to size your range.";

/**
 * A failed step validation: the message, and WHERE it belongs.
 *
 * A complaint about ONE field renders against that field and takes focus with
 * it; anything describing the step as a whole keeps the page-level slot below
 * the question card.
 */
/** A step complaint. `field` names the control it belongs beside, so the
 *  message renders where the answer is typed and the cursor can be put there;
 *  an omitted field renders the message at the foot of the step. */
/** A step's complaint, optionally pointed at the field that fixes it.
 *
 *  🔴 `field` is a CLOSED union on purpose. Every member needs three things
 *  wired — a ref, a focus arm in `advance`, and a condition-scoped error
 *  derivation — and a free `string` would let a gate name a field with none of
 *  them: the complaint would render only in the generic slot at the bottom of
 *  the step, the cursor would not move, and the message would sit red under a
 *  field the buyer had already corrected. Widening this is the compile error
 *  that reminds you to wire the other three. */
type StepValidation = {
  message: string;
  field?: "cash" | "target-price" | "target-cash-flow" | "target-business" | "operator-name";
};

export interface BuyerForm {
  firstName: string;
  lastName: string;
  phone: string;
  targetIndustry: string;
  /** The ONE business the buyer already has in mind, if any. Optional — a buyer
   *  still shopping a category leaves it blank, which is the common case. */
  targetBusinessName: string;
  homeCity: string;
  /** Which arm the buyer chose: `"LetterOnly"`, `"LetterAndDealSheet"` or
   *  `"InternalBuyer"`. REQUIRED — the step will not advance while it is null,
   *  because it decides which documents are generated AND, on the third arm,
   *  what the qualification is sized from; a default would be Phoenix choosing
   *  on the buyer's behalf. */
  documentPath: string | null;
  priceBand: string | null;
  /** The three answers an INTERNAL buyer gives about how they are connected to
   *  the business. Blank on the other two paths and never submitted from them —
   *  see the answers memo, which clears them for the same reason it clears the
   *  deal figures.
   *
   *  `internalRelationship` is REQUIRED on the internal path. The other two are
   *  optional, held as digit strings, and gate nothing: there is no minimum
   *  tenure, and the co-buyer count is a staff-side fact that prints on no
   *  document. */
  internalRelationship: string | null;
  internalYearsWithBusiness: string;
  internalCoBuyerCount: string;
  /** The figures a named-deal buyer states about the business. Held as the
   *  grouped digit strings the currency fields use, and the date as the
   *  `YYYY-MM-DD` a date input emits.
   *
   *  BLANK on the letter-only path and never submitted from it — see the
   *  answers memo, which clears them rather than trusting them to be empty, so
   *  a buyer who fills the deal fields and then switches to letter-only ships
   *  nothing they retracted. */
  targetPurchasePrice: string;
  targetAnnualCashFlow: string;
  /** ⚠️ RETIRED AS A QUESTION, KEPT AS A FIELD. No control writes this any
   *  more and the answers memo sends null unconditionally; `hydrateBuyerForm`
   *  still restores it so the hydration map stays complete and a resumed
   *  pre-retirement draft does not lose the value from STATE. Nothing reads it
   *  back out. See the memo’s own note for why the wire member survives. */
  targetDownPayment: string;
  targetPurchaseDate: string;
  cashAvailable: string;
  /** Which non-cash categories the buyer has ticked. A category that is not
   *  ticked is ABSENT from the submitted payload — never sent as 0. */
  otherAssetSelected: Partial<Record<OtherAssetKey, boolean>>;
  /** Per-category amounts, as the grouped digit strings the currency fields
   *  hold (same convention as `cashAvailable`). */
  otherAssetAmounts: Partial<Record<OtherAssetKey, string>>;
  /** The ONE shared note for the whole section — not one per category. */
  otherAssetsNote: string;
  /** Working capital after closing, as the grouped digit string the currency
   *  field holds. Optional, and a typed ZERO is a real answer. Sizes nothing:
   *  only `reservesBand` below it feeds the reserves factor ladder. */
  reservesAmount: string;
  reservesBand: string | null;
  ownershipExperience: string;
  managementExperience: string;
  industryFit: string;
  /** "operator" | "oversight-manager" | "hands-off" — the active-management
   *  gate + the involvement sizing factor. */
  involvement: string;
  /** The AAMA answer ("" | "yes" | "no"); asked only of a non-operator. Maps to
   *  answers.aamaPath ("yes" → true). For an individual overseer it is half of
   *  the five-choice full-time-manager answer ({@link managerChoiceOf}): "yes"
   *  is the AAMA choice and "no" is "Not decided yet". */
  aamaAnswer: string;
  /** "near" | "region" | "anywhere" — where the buyer is open to operating. */
  operatingGeographyMode: string;
  knockouts: Record<string, string>;
  goals: string;
  email: string;
  // ── Company & sponsor buyers (owner brief 2026-10-08, D1-D4, D9) ──────────
  /** "Who is buying?" — a `PrequalBuyerType` member name, or null (= not asked =
   *  an individual). Asked first on the target step, on every arm. */
  buyerType: string | null;
  /** The acquiring company and the person's title in it — required for a
   *  company type, asked beside the name on the review step. Website and
   *  "about" are optional. Kept while the type is switched away and back;
   *  the answers memo is what keeps a retracted value off the wire. */
  acquiringCompanyName: string;
  acquiringCompanyTitle: string;
  acquiringCompanyWebsite: string;
  acquiringCompanyAbout: string;
  /** Where a company's equity contribution comes from (D3) — a
   *  `PrequalEquitySource` member name, or null. Optional everywhere: it sizes
   *  nothing and never blocks the internal arm. */
  equitySource: string | null;
  /** Who will manage the business full time when the buyer will not (FT) — a
   *  `PrequalOperatorArrangement` member name, or null — asked of a company
   *  buyer who is not the operator (D4) and of an individual overseer — and the
   *  designated operator's name: required for a principal, existing management
   *  or another designated person, optional for a general manager still to be
   *  hired. */
  operatorArrangement: string | null;
  operatorName: string;
}

/** The acquiring company's field limits, refused (never truncated) server-side.
 *  Mirror of `BuyerAcquirer.Max*Length` (Core/Common/BuyerAcquirer.cs), pinned by
 *  BuyerPrequalifyPage.test.tsx reading that file. */
export const COMPANY_FIELD_LIMITS = {
  companyName: 200,
  title: 150,
  website: 300,
  about: 600,
  operatorName: 200,
} as const;

/** The full-time-manager arrangements each buyer may give (D3): a company keeps
 *  its three; an individual overseer has no "principal of the acquiring
 *  company" and may designate another person instead. */
const COMPANY_ARRANGEMENTS: readonly PrequalOperatorArrangement[] = [
  "CompanyPrincipal",
  "HiredGeneralManager",
  "ExistingManagement",
];
const INDIVIDUAL_ARRANGEMENTS: readonly PrequalOperatorArrangement[] = [
  "HiredGeneralManager",
  "ExistingManagement",
  "DesignatedPerson",
];

/** The arrangements that satisfy the requirement only with a name — the
 *  server's predicate (`BuyerAcquirer.DesignatedOperatorSatisfied`). */
const NAME_REQUIRED: readonly PrequalOperatorArrangement[] = [
  "CompanyPrincipal",
  "ExistingManagement",
  "DesignatedPerson",
];

/** Said when the name a designated operator needs is missing (FT). */
export const OPERATOR_NAME_MISSING = "Tell us who will manage the business full time.";

/**
 * Which full-time-management follow-up the background step asks (FT).
 *
 *  - `none` — the buyer is the operator.
 *  - `company` — a company buyer who is not: who will manage it (its three
 *    choices and a name), then the AAMA question.
 *  - `overseer` — an individual who will oversee it: the requirement, then ONE
 *    five-choice question, AAMA among the choices (ruling Q1).
 *  - `hands-off` — any other individual (hands-off, or an involvement this
 *    build does not know): the hands-off requirement and the AAMA question
 *    only (D2) — the server never lets them designate.
 */
type ManagementFollowUp = "none" | "company" | "overseer" | "hands-off";

function managementFollowUp(companyBuyer: boolean, involvement: string): ManagementFollowUp {
  if (involvement === "operator") return "none";
  if (companyBuyer) return "company";
  return involvement === "oversight-manager" ? "overseer" : "hands-off";
}

/** The individual overseer's five choices — three arrangements (their wire
 *  names) and the two that designate nobody. */
type ManagerChoice = PrequalOperatorArrangement | "aama" | "undecided";

/** The five-choice answer the form holds, read from the fields it is stored in:
 *  an individual arrangement first (the designated operator wins over AAMA,
 *  ruling Q1), then AAMA "yes", then "no" as "Not decided yet". */
export function managerChoiceOf(
  form: Pick<BuyerForm, "operatorArrangement" | "aamaAnswer">,
): ManagerChoice | null {
  const arrangement = asOperatorArrangement(form.operatorArrangement, false);
  if (arrangement) return arrangement;
  if (form.aamaAnswer === "yes") return "aama";
  if (form.aamaAnswer === "no") return "undecided";
  return null;
}

/** The form after a five-choice pick. AAMA keeps today's geography widening —
 *  a buyer installing a manager need not live by the business. */
function chooseManager(f: BuyerForm, choice: string): BuyerForm {
  if (choice === "aama") {
    return { ...f, operatorArrangement: null, operatorName: "", aamaAnswer: "yes", operatingGeographyMode: "anywhere" };
  }
  if (choice === "undecided") return { ...f, operatorArrangement: null, operatorName: "", aamaAnswer: "no" };
  return { ...f, operatorArrangement: choice, aamaAnswer: "" };
}

/**
 * The form after a change to the buyer type or the involvement, with the
 * follow-up answers that no longer describe the question being asked cleared.
 *
 * Unchanged follow-up → nothing cleared. The buyer becomes the operator → every
 * follow-up answer goes. Otherwise the arrangement and its name go (no
 * DesignatedPerson for a company, no principal for an individual, no operator
 * answer at all for a hands-off individual) and only an AAMA "yes" survives:
 * it means the same thing in every follow-up, where a "no" ("Neither", or "Not
 * decided yet") was an answer to a different question.
 */
function withFollowUpsFor(prev: BuyerForm, next: BuyerForm): BuyerForm {
  const before = managementFollowUp(isCompanyTypeAnswer(asBuyerType(prev.buyerType)), prev.involvement);
  const after = managementFollowUp(isCompanyTypeAnswer(asBuyerType(next.buyerType)), next.involvement);
  if (before === after) return next;
  return {
    ...next,
    operatorArrangement: null,
    operatorName: "",
    aamaAnswer: after !== "none" && next.aamaAnswer === "yes" ? "yes" : "",
  };
}

/** What the follow-up puts on the wire: only answers to the question actually
 *  asked, so a retracted one stays in the form and off the wire. */
function managementAnswers(
  form: BuyerForm,
  followUp: ManagementFollowUp,
): Pick<PrequalBuyerAnswers, "aamaPath" | "operatorArrangement" | "operatorName"> {
  const named = (arrangement: PrequalOperatorArrangement | null) =>
    arrangement ? form.operatorName.trim() || null : null;
  switch (followUp) {
    case "company": {
      const arrangement = asOperatorArrangement(form.operatorArrangement, true);
      return { aamaPath: form.aamaAnswer === "yes", operatorArrangement: arrangement, operatorName: named(arrangement) };
    }
    case "overseer": {
      const choice = managerChoiceOf(form);
      const arrangement = choice === "aama" || choice === "undecided" ? null : choice;
      return { aamaPath: choice === "aama", operatorArrangement: arrangement, operatorName: named(arrangement) };
    }
    case "hands-off":
      return { aamaPath: form.aamaAnswer === "yes", operatorArrangement: null, operatorName: null };
    case "none":
      return { aamaPath: false, operatorArrangement: null, operatorName: null };
  }
}

/**
 * The form's `documentPath` narrowed to the wire enum, or null.
 *
 * The form holds a plain string because that is what a choice control deals in,
 * and the two legal values are the SERVER's own member names. Narrowing rather
 * than casting means an unrecognised value — a hand-edited sessionStorage blob,
 * a stale draft written by an older build — resolves to null and the buyer is
 * asked the question again, instead of a string the server has no reading for
 * being written into a string-stored column.
 */
function asDocumentPath(raw: string | null): PrequalBuyerDocumentPath | null {
  return raw === DOCUMENT_PATH_LETTER_ONLY ||
    raw === DOCUMENT_PATH_WITH_DEAL_SHEET ||
    raw === DOCUMENT_PATH_INTERNAL
    ? raw
    : null;
}

/**
 * The form's `internalRelationship` narrowed to the wire enum, or null. Same
 * reasoning as `asDocumentPath`: the control deals in strings, the three legal
 * values are the SERVER's own member names, and an unrecognised one — a
 * hand-edited blob, a draft from an older build — resolves to null and the
 * question is asked again rather than a value the server cannot read being
 * written into a string-stored column.
 */
function asInternalRelationship(raw: string | null): PrequalInternalRelationship | null {
  return INTERNAL_RELATIONSHIP_OPTIONS.some((o) => o.value === raw)
    ? (raw as PrequalInternalRelationship)
    : null;
}

/** The three company-buyer choices narrowed to their wire enums, or null — the
 *  same reasoning as `asDocumentPath`: an unrecognised value from a stale blob or
 *  an older build is asked again rather than shipped to a strict reader. */
function asBuyerType(raw: string | null): PrequalBuyerType | null {
  return raw === "Individual" || raw === "CompanyOwnedOrLed" || raw === "InvestmentGroupOrFund"
    ? raw
    : null;
}

function asEquitySource(raw: string | null): PrequalEquitySource | null {
  return raw === "CompanyFundsOnHand" || raw === "CommittedByInvestors" || raw === "StillBeingRaised"
    ? raw
    : null;
}

function asOperatorArrangement(raw: string | null, company: boolean): PrequalOperatorArrangement | null {
  return (company ? COMPANY_ARRANGEMENTS : INDIVIDUAL_ARRANGEMENTS).find((a) => a === raw) ?? null;
}

/** A whole-number field as a number, or null when it is blank or not one.
 *  Negative is not a count of anything, so it reads as absent. */
function parseWholeNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && Number.isInteger(value) && value >= 0 ? value : null;
}

export const initialForm: BuyerForm = {
  firstName: "",
  lastName: "",
  phone: "",
  targetIndustry: "",
  targetBusinessName: "",
  homeCity: "",
  documentPath: null,
  priceBand: null,
  internalRelationship: null,
  internalYearsWithBusiness: "",
  internalCoBuyerCount: "",
  targetPurchasePrice: "",
  targetAnnualCashFlow: "",
  targetDownPayment: "",
  targetPurchaseDate: "",
  cashAvailable: "",
  otherAssetSelected: {},
  otherAssetAmounts: {},
  otherAssetsNote: "",
  reservesAmount: "",
  reservesBand: null,
  ownershipExperience: "never-owned",
  managementExperience: "none",
  industryFit: "new",
  involvement: "operator",
  aamaAnswer: "",
  operatingGeographyMode: "near",
  knockouts: {},
  goals: "",
  email: "",
  buyerType: null,
  acquiringCompanyName: "",
  acquiringCompanyTitle: "",
  acquiringCompanyWebsite: "",
  acquiringCompanyAbout: "",
  equitySource: null,
  operatorArrangement: null,
  operatorName: "",
};

/** §2 — per-step PRIMARY-field predicates: the AI guide never fires for a
 *  step whose primary fields are still empty (no tips about empty fields).
 *
 *  `hasCapital` is passed in rather than derived here — it is the SAME boolean
 *  the meter and the step gate run on, so all three read one value instead of
 *  three expressions that agreed on the day they were written. This was the
 *  third copy of the cash-only rule: a buyer with no cash and $100,000 of
 *  business equity got no tip on the very step that had just stopped
 *  turning them away. */
function stepPrimaryFilled(
  stepKey: string,
  form: BuyerForm,
  hasCapital: boolean,
): boolean {
  switch (stepKey) {
    case "start":
      // The fork is this step's ONLY question, so there is nothing else a tip
      // could be about. False until it is answered; the moment it is, the buyer
      // is on their way to the next step anyway, so this is close to inert —
      // which is the honest answer rather than an omission.
      return form.documentPath !== null;
    case "target":
      // Any of the three things this step asks for across its arms. The fork is
      // NOT one of them any more — it is answered on the step before, so it is
      // true for every visitor here and would make this predicate a constant.
      return (
        form.targetIndustry.trim() !== "" ||
        form.targetBusinessName.trim() !== "" ||
        form.priceBand !== null ||
        parseMoney(form.targetPurchasePrice) !== null
      );
    case "capital":
      return hasCapital;
    case "background":
      // The three ladders and the involvement question all ship with defaults
      // pre-selected — the step's primary selections are always made.
      return true;
    case "eligibility":
      return Object.keys(form.knockouts).length > 0;
    case "review":
      // Contact capture gets no tip, but GOALS lives here now and did get one
      // when it had its own step. Keyed on the goals box so the tip still fires
      // for a buyer who fills it in and never fires about an empty one.
      return form.goals.trim() !== "";
    default:
      // 🔴 UNREACHABLE, AND THE POINT IS THAT IT SAYS SO. Every key in
      // BUYER_STEPS has an arm above. This arm is what a NEW step falls into,
      // and its consequence is silent: no tip, ever, on that step, with nothing
      // thrown and nothing logged. If you add a step, add an arm.
      return false;
  }
}

function clampStep(index: number): number {
  return Math.min(Math.max(0, Math.trunc(index)), BUYER_STEPS.length - 1);
}

/** Dollars → the grouped digit string the currency fields hold. Zero becomes
 *  BLANK, not "0": a zero on the wire means the buyer entered nothing, and
 *  showing them a literal "0" they never typed would be putting words in their
 *  mouth. (It no longer decides whether the meter runs — that is keyed on the
 *  buyer's TOTAL down-payment capacity, cash plus every named asset.) */
function moneyToField(amount: number | null | undefined): string {
  return amount && amount > 0 ? formatMoneyTyping(String(amount)) : "";
}

/**
 * The same conversion, except that a stored 0 comes back as a visible "0"
 * rather than a blank field.
 *
 * Two callers, both in `hydrateBuyerForm`: the buyer’s working capital after
 * closing, and the retired cash-at-closing figure. In both, a stored 0 is a real
 * answer somebody gave — "I will have nothing left" and "I am financing all of
 * it" — and a resume must not turn either into "did not say".
 *
 * ⚠️ The cash-at-closing caller is now hydration-only: the field it fed is
 * retired, nothing renders the value and the answers memo sends null. It stays
 * because the hydration map has to be complete, not because the figure is still
 * in play.
 */
function moneyToFieldKeepingZero(amount: number | null | undefined): string {
  return amount == null ? "" : formatMoneyTyping(String(amount));
}

/** A whole-number answer back into its field. Blank for absent AND for a
 *  stored 0, because zero years with a business is not something an employee
 *  can truthfully be — the field means "how long", and 0 there is a blank. */
function wholeNumberToField(value: number | null | undefined): string {
  return value != null && value > 0 ? String(value) : "";
}

/** The same, except a stored 0 comes back as a visible "0". Used for the
 *  co-buyer count, where zero means "nobody is buying in with me" — a real
 *  answer, and a different claim from having declined to say. */
function wholeNumberToFieldKeepingZero(value: number | null | undefined): string {
  return value == null ? "" : String(value);
}

/**
 * What one non-cash category is worth, or null when it is unticked, blank or
 * zero.
 *
 * ONE rule, read by all three places that need it — the live meter's payload,
 * the submitted payload, and the capital step's validation — so they can never
 * disagree about whether a category counts. Zero collapses to "not named" on
 * purpose: the engines distinguish "category not selected" from "selected and
 * worth $0", and a $0 asset would print on the letter as though it were one.
 */
function namedAssetAmount(form: BuyerForm, key: OtherAssetKey): number | null {
  if (!form.otherAssetSelected[key]) return null;
  const amount = parseMoney(form.otherAssetAmounts[key] ?? "");
  return amount !== null && amount > 0 ? amount : null;
}

/**
 * Form → the optional non-cash assets block on the wire.
 *
 * NULL when nothing usable is named, and that is the point: an application
 * with no non-cash assets serializes exactly as it did before this section
 * existed, so a cash-only buyer's figures are byte-identical. A zeroed object
 * would instead put five selected-but-empty categories on every one of them.
 */
function buildCashEquivalents(form: BuyerForm): PrequalCashEquivalents | null {
  const named = OTHER_ASSET_CATEGORIES.map(
    (key) => [key, namedAssetAmount(form, key)] as const,
  ).filter((pair): pair is readonly [OtherAssetKey, number] => pair[1] !== null);

  if (named.length === 0) return null;

  const equivalents: PrequalCashEquivalents = {};
  for (const [key, amount] of named) equivalents[key] = amount;
  const note = form.otherAssetsNote.trim();
  if (note) equivalents.note = note;
  return equivalents;
}

/** Wire → form. Only a category carrying a real figure comes back ticked, so a
 *  resumed draft shows exactly what was named and nothing else. */
function otherAssetsFromAnswers(
  equivalents: PrequalCashEquivalents | null | undefined,
): Pick<BuyerForm, "otherAssetSelected" | "otherAssetAmounts" | "otherAssetsNote"> {
  const otherAssetSelected: Partial<Record<OtherAssetKey, boolean>> = {};
  const otherAssetAmounts: Partial<Record<OtherAssetKey, string>> = {};
  for (const key of OTHER_ASSET_CATEGORIES) {
    const amount = equivalents?.[key];
    if (amount != null && amount > 0) {
      otherAssetSelected[key] = true;
      otherAssetAmounts[key] = moneyToField(amount);
    }
  }
  return { otherAssetSelected, otherAssetAmounts, otherAssetsNote: equivalents?.note ?? "" };
}

/**
 * Server answers → funnel form.
 *
 * 🔴 EVERY ANSWER THE FORM CAN HOLD MUST BE LISTED HERE, and an omission is
 * SILENT: the spread of `current` leaves an unlisted field at its `initialForm`
 * value, so a resumed draft simply arrives without it and finalizes without it.
 * This map is the whole of the cross-device story — `usePrequalDraftSession`
 * applies it exactly when there is no local blob, which is the resume the server
 * draft exists for, plus the reload after a save conflict.
 *
 * Two fields are deliberately lossy in the safe direction. `aamaAnswer` is
 * restored only for an explicit yes: `aamaPath: false` cannot tell "answered
 * no" from "never asked", and fabricating a "no" would soft-decline somebody
 * on an answer they never gave. So an individual overseer's five-choice answer
 * ({@link managerChoiceOf}) comes back from the stored arrangement, else from
 * AAMA "yes", else unanswered — "Not decided yet" is not restored. `phone` is
 * not answer content, is not on the draft at all, and simply comes back blank
 * on another device. Those two are the
 * ONLY lossy ones; anything else missing from this map is a defect.
 */
export function hydrateBuyerForm(current: BuyerForm, loaded: PrequalDraftLoad): BuyerForm {
  const a = loaded.answers as PrequalBuyerAnswers;
  return {
    ...current,
    firstName: loaded.identity.firstName || current.firstName,
    lastName: loaded.identity.lastName || current.lastName,
    email: loaded.identity.email || current.email,
    targetIndustry: a.targetIndustry ?? "",
    targetBusinessName: a.targetBusinessName ?? "",
    homeCity: a.operatingGeography?.homeCity ?? "",
    priceBand: a.priceBand ?? null,
    // 🔴 THE PACKAGE FORK AND THE FOUR FIGURES BEHIND IT. Omitting them does
    // not merely lose typing: `documentPath` comes back null, the target step is
    // the only thing that gates on it, and the assisted surface opens at the
    // FINALIZE step — so an operator can submit a resumed draft without the
    // target step ever rendering. The buyer's explicit choice would silently
    // revert to "not asked", which every server predicate reads as "wanted
    // both", and a deal-path buyer's named business would vanish from their
    // Deal Sheet while the sheet itself still generated.
    documentPath: a.documentPath ?? null,
    // 🔴 THE SAME TRAP, ONE PATH OVER. `internalRelationship` is the internal
    // arm's one REQUIRED answer and the thing its letter names; omitting it here
    // would bring a resumed draft back with the question un-answered, and the
    // assisted surface opens at the FINALIZE step, so nothing would re-ask it.
    // The other two are context rather than gates, and losing them is quieter
    // still — which is exactly why they are listed rather than trusted.
    internalRelationship: a.internalRelationship ?? null,
    internalYearsWithBusiness: wholeNumberToField(a.internalYearsWithBusiness),
    // 🔴 NOT the blank-on-zero rule. "Nobody is buying in with me" is a real
    // answer and a different claim from "did not say"; round-tripping a stored 0
    // through a blank would turn a buyer purchasing alone into one who declined
    // to say how many partners they have.
    internalCoBuyerCount: wholeNumberToFieldKeepingZero(a.internalCoBuyerCount),
    targetPurchasePrice: moneyToField(a.targetPurchasePrice),
    targetAnnualCashFlow: moneyToField(a.targetAnnualCashFlow),
    // ⚠️ HYDRATION-ONLY SINCE THE FIELD WAS RETIRED. It is listed because an
    // omission from this map is SILENT and the map’s completeness is the whole
    // contract (see the block comment above); it is NOT listed because the value
    // still travels. Nothing renders it and the answers memo sends null, so a
    // resumed draft restores the figure into state and finalizes without it.
    // `moneyToFieldKeepingZero` rather than `moneyToField` for the same reason
    // it always was — a stored 0 was a real answer — which now only matters to
    // whatever reads this field next, and today nothing does.
    targetDownPayment: moneyToFieldKeepingZero(a.targetDownPayment),
    targetPurchaseDate: a.targetPurchaseDate ?? "",
    cashAvailable: moneyToField(a.cashAvailable),
    ...otherAssetsFromAnswers(a.cashEquivalents),
    // NOT `moneyToField`, which returns "" for 0 — a buyer who says they will
    // have nothing left after closing has answered, and a resume must not turn
    // that into "did not say". Same helper as `targetDownPayment` above, and
    // unlike that one this field is still asked for and still submitted.
    reservesAmount: moneyToFieldKeepingZero(a.reservesAmount),
    reservesBand: a.reservesBand ?? null,
    ownershipExperience: a.ownershipExperience || initialForm.ownershipExperience,
    managementExperience: a.managementExperience || initialForm.managementExperience,
    industryFit: a.industryFit || initialForm.industryFit,
    involvement: a.involvement || initialForm.involvement,
    aamaAnswer: a.aamaPath ? "yes" : "",
    operatingGeographyMode: a.operatingGeography?.mode ?? initialForm.operatingGeographyMode,
    knockouts: a.knockouts ?? {},
    goals: a.goals ?? "",
    // Company & sponsor buyers — every one restored, for the reason above: an
    // omission resumes silently at the initial value, and the assisted surface
    // opens at the FINALIZE step, where a lost company name would reach the
    // server as an individual's application.
    buyerType: a.buyerType ?? null,
    acquiringCompanyName: a.acquiringCompanyName ?? "",
    acquiringCompanyTitle: a.acquiringCompanyTitle ?? "",
    acquiringCompanyWebsite: a.acquiringCompanyWebsite ?? "",
    acquiringCompanyAbout: a.acquiringCompanyAbout ?? "",
    equitySource: a.equitySource ?? null,
    operatorArrangement: a.operatorArrangement ?? null,
    operatorName: a.operatorName ?? "",
  };
}

export function BuyerPrequalifyPage({
  adapter: injectedAdapter,
  finalizeSlot,
}: PrequalFunnelPageProps = {}) {
  usePrequalAttributionCapture("prequalify-buyer");
  const navigate = useNavigate();
  // Whatever the flow resolved: the route's `:slug` on the public funnel, the
  // DRAFT's site under an assisted surface. This page does not decide, and so
  // cannot be the surface that forgets.
  const { brand, loading: brandPending, notFound } = usePartnerBrand();
  // Server-published fine print, falling back to the compiled-in constants.
  const { administratorLine, indicativeLabel, marketing, questions } = useFunnelCopy();
  // Every buyer question, option label and helper is a Document Language block
  // (D7), published-else-default. A blank one drops its element (D4); the
  // control it labels stays.
  const q: QuestionCopy = (block) => questions[block];
  const invite = usePrequalInvite();
  // The /buy landing page's continuation token. Read here rather than inside
  // the finalize builder because it is also part of the session scope below,
  // and one read keeps the two from ever disagreeing about which run this is.
  const buyLandingToken = usePrequalBuyLandingToken();
  // The three answers `/buy` already took, carried across in sessionStorage
  // rather than on the URL — see useBuyLandingPrefill for why not the token,
  // and for why the blob is dropped when the run ends rather than when it is
  // read. Both run-end paths below call `forgetBuyPrefill`.
  const [buyPrefill, forgetBuyPrefill] = useBuyLandingPrefill();
  // The per-advertisement link token (`?ad=`). Read beside `blt` but NOT part
  // of the session scope below: it names an advertisement, not an applicant,
  // and two people handed one ad link are still two applications the scope
  // already tells apart by everything else. See usePrequalAdToken.
  const advertisementToken = usePrequalAdToken();
  const base = usePrequalBase();
  const partnerSlug = usePrequalPartnerSlug();

  // WHOSE application this tab is holding. Captured ONCE, at mount, from the
  // synchronous halves of the URL — the raw route slug (not the resolved
  // brand, which arrives asynchronously and would restore first and check
  // second), the invite token, and the /buy continuation token. Frozen
  // deliberately: if the address bar's scope drifts under a live mount, the
  // answers on screen still belong to the context they were typed in, and the
  // next mount is what re-decides.
  //
  // `blt` belongs here for the same reason the invite token does — two people
  // handed the same tab are two applications, and a continuation token names
  // one of them. It costs nothing to carry: nothing in this funnel navigates
  // between steps (they are component state, not routes), so the param cannot
  // be dropped out from under a live run.
  const [scope] = useState<PrequalSessionScope>(() => ({
    partnerSlug,
    inviteToken: invite.token,
    buyLandingToken,
  }));

  // §7 — restore any in-progress (or finalized) state from sessionStorage,
  // but ONLY this context's. A blob from another partner site or another
  // invite is somebody else's name, email and answers; it is removed rather
  // than skipped. An invalid/stale blob is ignored (the store shape-checks).
  const [restored] = useState(() =>
    loadScopedFunnelState<Partial<BuyerForm>>(PREQUAL_BUYER_STORAGE_KEY, scope),
  );
  const [form, setForm] = useState<BuyerForm>(() =>
    restored ? { ...initialForm, ...restored.form } : initialForm,
  );
  const [stepIndex, setStepIndex] = useState(() => (restored ? clampStep(restored.stepIndex) : 0));
  const [maxReached, setMaxReached] = useState(() =>
    restored ? clampStep(restored.maxReached) : 0,
  );
  const [finalized, setFinalized] = useState<PrequalFinalizeResult | null>(
    () => restored?.finalized ?? null,
  );
  const [stepError, setStepError] = useState<StepValidation | null>(null);
  // The cash input, so a capital complaint can put the cursor where the fix is.
  // CurrencyField mints its own id (the `$` overlay forces it), so a ref is the
  // only handle on the element from out here.
  const cashInputRef = useRef<HTMLInputElement>(null);
  const targetPriceInputRef = useRef<HTMLInputElement>(null);
  const targetCashFlowInputRef = useRef<HTMLInputElement>(null);
  const targetBusinessInputRef = useRef<HTMLInputElement>(null);
  const operatorNameInputRef = useRef<HTMLInputElement>(null);

  // A COMPLETED application is never silently reopened. Landing back on the
  // funnel root holding one is ambiguous — the same person coming back for
  // their documents, and the next client in the chair, arrive at the same URL
  // — so it asks instead of guessing. Dismissed in memory only: choosing to
  // adjust must not cost them the completed copy the Result route reads.
  const [completedGateDismissed, setCompletedGateDismissed] = useState(false);
  const completedOnMount = restored?.finalized ?? null;

  // The persistence seam. The anonymous adapter is built unconditionally
  // (hooks cannot be conditional) but is pure construction and fires nothing,
  // so it is inert whenever the assisted surface has injected its own.
  const anonymousAdapter = useAnonymousPrequalDraftAdapter("Buyer");
  const adapter = injectedAdapter ?? anonymousAdapter;
  // Assisted = somebody is driving this on the applicant's behalf. It trims the
  // chrome (see PortalShell's `compact`) and moves the save note to step one.
  // The PUBLIC funnel is anonymous and every one of these gates is false there.
  const assisted = adapter.mode === "assisted";
  const draft = usePrequalDraftSession({
    adapter,
    hasLocalState: restored !== null,
    onLoaded: (loaded) => {
      setForm((f) => hydrateBuyerForm(f, loaded));
      setStepIndex(clampStep(loaded.stepIndex));
      setMaxReached((m) => Math.max(m, clampStep(loaded.stepIndex)));
    },
  });

  // sessionStorage stays exactly what it was: the same-tab fallback that keeps
  // the funnel working when the draft calls fail, and the store the Result
  // route reads. The server draft is the cross-device copy, not a replacement.
  useEffect(() => {
    saveFunnelState(PREQUAL_BUYER_STORAGE_KEY, { form, stepIndex, maxReached, finalized }, scope);
  }, [form, stepIndex, maxReached, finalized, scope]);

  // ONE derivation of "did this buyer ask for a priced deal", read by the memo
  // below, by the step gate, and by the render. Three copies of a predicate that
  // agreed on the day they were written is a shape this funnel has been bitten
  // by before (see `stepPrimaryFilled`'s own note about the cash-only rule).
  const onDealPath = form.documentPath === DOCUMENT_PATH_WITH_DEAL_SHEET;
  // 🔴 A SEPARATE PREDICATE, never `!onLetterOnly`. The internal arm shares the
  // deal arm's FIGURES and shares none of its rules: those figures are required
  // rather than optional, the band is derived from the price on both, and the
  // capital step gates on one and must not gate on the other. Folding the two
  // into one boolean is how a rule written for one arm silently acquires the
  // other — every path predicate below names the arm it means.
  const onInternalPath = form.documentPath === DOCUMENT_PATH_INTERNAL;
  // The two arms that state a price for a specific business. Shared strictly
  // where the SAME rule applies to both, and nowhere else.
  const statesADeal = onDealPath || onInternalPath;
  // Company & sponsor buyers: the TYPE alone, because the company name is asked
  // last (on the review step). The server's predicate also needs the name, and
  // finalize refuses a company type without one, so by submit the two agree.
  const companyBuyer = isCompanyTypeAnswer(asBuyerType(form.buyerType));
  // Who will manage the business full time is asked of a company buyer who will
  // not run it themselves (D4) and of an individual overseer (FT); a hands-off
  // individual is asked only the AAMA question (D2).
  const followUp = managementFollowUp(companyBuyer, form.involvement);
  const managerChoice = followUp === "overseer" ? managerChoiceOf(form) : null;
  const operatorArrangement =
    followUp === "company" || followUp === "overseer"
      ? asOperatorArrangement(form.operatorArrangement, followUp === "company")
      : null;
  const operatorNameMissing =
    operatorArrangement !== null &&
    NAME_REQUIRED.includes(operatorArrangement) &&
    form.operatorName.trim() === "";

  const answers: PrequalBuyerAnswers = useMemo(() => {
    return {
      targetIndustry: form.targetIndustry.trim() || null,
      targetBusinessName: form.targetBusinessName.trim() || null,
      documentPath: asDocumentPath(form.documentPath),
      // 🔴 THE BAND IS DERIVED ON THE DEAL PATH, NOT DROPPED. That buyer is
      // never shown the band question — they have already given a specific
      // price — but the band is read downstream by things they never see
      // (BuyerLead.StatedPriceBand, the listing-research engine's price filter,
      // the analysis document's stated-target label), so skipping the question
      // and shipping null would cost those an answer the buyer has in fact
      // given more precisely. Out-of-range prices derive to null, which is the
      // honest result: no band describes them.
      priceBand: statesADeal
        ? priceBandForAmount(parseMoney(form.targetPurchasePrice))
        : form.priceBand,
      // 🔴 CLEARED OFF THE INTERNAL PATH for the same reason the deal figures
      // are cleared off the letter-only one: a buyer may answer these, change
      // their mind and switch arms, and the fields are then hidden with their
      // values retained. Gating here is what keeps a retracted answer off the
      // wire. The relationship is narrowed rather than passed through, so a
      // stale draft cannot ship a value the server has no reading for.
      internalRelationship: onInternalPath
        ? asInternalRelationship(form.internalRelationship)
        : null,
      // Tenure is asked of an employee or manager and of nobody else, so it is
      // gated on the RELATIONSHIP as well as the path — a buyer who answered it
      // and then switched to "I'm family" must not still be claiming a tenure.
      internalYearsWithBusiness:
        onInternalPath &&
        asInternalRelationship(form.internalRelationship) === INTERNAL_RELATIONSHIP_WITH_TENURE
          ? parseWholeNumber(form.internalYearsWithBusiness)
          : null,
      internalCoBuyerCount: onInternalPath
        ? parseWholeNumber(form.internalCoBuyerCount)
        : null,
      // 🔴 CLEARED OFF THE DEAL PATH RATHER THAN TRUSTED TO BE BLANK. A buyer
      // may fill these in, change their mind, and switch to letter-only; the
      // fields are then hidden and their values retained (so a switch BACK does
      // not lose their typing), which means "the form holds a figure" stops
      // meaning "the buyer is claiming it". Gating here is what keeps a
      // retracted answer off the wire, and it is what makes the server's
      // "LetterOnly carries no deal" reading true rather than hoped for.
      //
      // ZERO IS NOT UNIFORM ACROSS THE THREE, and the asymmetry is the point:
      // nobody buys a business for nothing and no business earns nothing, so a
      // typed 0 in either of those is a blank field and normalizes to null —
      // but Phoenix requires no down payment and sets no minimum, so 0 there is
      // a real answer about an all-financed structure and is sent verbatim.
      //
      // 🔴 THE SAME `nullIfNotPositive` SERVES TWO DIFFERENT RULES. On the deal
      // arm it NORMALISES — a zero price is a blank field and the server accepts
      // the null. On the internal arm the field itself refuses blank and zero
      // before the buyer can advance, and the finalize boundary refuses them
      // again, so by the time this runs there is nothing left for it to rewrite:
      // here it is a belt, not the gate. Do not "simplify" it away on the
      // strength of the field gate — the memo is also what an assisted operator
      // and a resumed draft go through.
      targetPurchasePrice: statesADeal
        ? nullIfNotPositive(parseMoney(form.targetPurchasePrice))
        : null,
      targetAnnualCashFlow: statesADeal
        ? nullIfNotPositive(parseMoney(form.targetAnnualCashFlow))
        : null,
      // 🔴 ALWAYS NULL — THE FIELD IS RETIRED AND THE MEMBER IS NOT. Nothing
      // in the funnel asks for cash at closing any more: Phoenix requires no
      // down payment and sets no minimum, so the question invited a buyer to
      // nominate a figure against a requirement that does not exist, and an
      // empty box under two required ones read as an omission rather than as
      // the answer it was. The review step shows the assumption back instead.
      //
      // 🔴 THIS MOVES A NUMBER ON THE INTERNAL ARM, AND IT IS THE ONE
      // CONSEQUENCE WORTH COSTING RATHER THAN DISCOVERING. `prequalSizing.ts`
      // reads `targetDownPayment` at exactly ONE site — `EvaluateBuyerOnBusiness`,
      // where `price = note + down` — so an internal buyer who used to state
      // $150,000 at closing qualified for a price $150,000 higher than they do
      // now. The capital arms never read it at all and are byte-unchanged.
      //
      // ✅ AND A FALL COULD CROSS A DECLINE THRESHOLD — CLOSED BY RULING R50,
      // WHICH IS WHY THE ENGINE STILL ISN'T TOUCHED HERE. The hazard was real:
      // `EvaluateBuyerOnBusiness` computed `high = Min(notes.High + down,
      // ProgramMaxPrice)` and then refused outright on `high < ProgramMinPrice`,
      // so where `notes.High` alone sat under the minimum but `notes.High +
      // stated` cleared it, a submission that used to PASS would now DECLINE —
      // terminal to the applicant, on a lead-capture funnel, for a question the
      // funnel had stopped asking.
      //
      // R50's answer is QUOTE THE BUSINESS, TEST ELIGIBILITY ON THE TRANSACTION.
      // The quote stays `note` (R49), and the below-minimum predicate alone
      // moved onto `PrequalBuyerAnswers.EligibilityCash()` — the wider of the
      // buyer's capacity and their stated plan. Because that term is `>=` the
      // `down` the old predicate used, the new ceiling is `>=` the old one for
      // EVERY submission, so the invariant NOBODY WHO PASSES TODAY DECLINES
      // TOMORROW holds by construction rather than by a population argument.
      // ⚠️ prequal-v7 (ruling Q4): the eligibility term is gone on new rows. T1
      // measures the minimum on the AMOUNT FINANCED, which the buyer's own cash
      // cannot raise, so the internal arm now declines iff the 20-year Maximum
      // Financed Amount is under the minimum. R50 survives only for v≤6 rows,
      // replayed server-side; the client mirror (`prequalSizing.ts`) runs the
      // live v7 rule and the two tiers must still agree.
      //
      // ✅ R3'S ENGINE HALF IS WITHDRAWN, so the "approved end-state" this
      // comment used to point at no longer exists. R49: pointing this arm at
      // `TotalDownPaymentCapacity()` was wrong in PRINCIPLE, not merely in its
      // collision with `PrequalInternalBuyerEngineTests
      // .A_wealthy_buyer_of_a_small_business_is_sized_on_the_business`. Capacity
      // is a fact about the buyer's wallet, and feeding it into `price = note +
      // down` makes a wealthy buyer's qualified price rise for the same business
      // with the same earnings — the capital arm's logic leaking across the
      // router, which is exactly what that test exists to prevent. Retiring the
      // question lets `down` fall to 0 and `price = note`, which IS "sized on
      // the business", so the engine needed no repoint at all.
      //
      // R37's Deal Sheet half stands unchanged: the document's closing row still
      // falls through to cash. Still do not close anything here by reviving the
      // retired field or by sending capacity from the client — the first re-asks
      // a question the owner retired, and the second decides in the client a
      // money-model question the rulings put in the engine.
      //
      // The direction is CONSERVATIVE (the qualified price falls, never rises)
      // and it is the reading that engine's own comment already calls correct:
      // "A BLANK DOWN PAYMENT IS ZERO ON THIS PATH — the premise of an internal
      // purchase is that no down payment is required, so deriving one would
      // invent the very thing the path exists to say is unnecessary." What
      // changes is that the funnel is now always blank there. If the ruling is
      // that an internal buyer's stated cash SHOULD raise their price, the fix
      // is a field on the internal arm and not a revival of this one — the arm
      // that reads it is not the arm the retired field sat on.
      //
      // The MEMBER stays on the wire: the server reads it for historical rows,
      // and removing it would make "this funnel no longer asks" and "an older
      // client sent nothing" indistinguishable.
      //
      // ⚠️ BE PRECISE ABOUT WHAT SURVIVES A RESUME, because "it still
      // round-trips" is the comfortable version and it is not true. The FIELD is
      // kept on `BuyerForm`, in `initialForm` and in `hydrateBuyerForm`, so a
      // resumed pre-retirement draft still restores its stored figure into form
      // state and the hydration map stays complete (an omission there is silent
      // — see its own note). But nothing renders it and nothing reads it, so on
      // finalize that buyer now ships null where they previously shipped 125,000.
      // That is the retirement doing what a retirement does: the alternative is
      // re-asserting, as a fresh claim, a figure the buyer was never shown and
      // cannot correct.
      targetDownPayment: null,
      targetPurchaseDate: statesADeal ? form.targetPurchaseDate.trim() || null : null,
      cashAvailable: parseMoney(form.cashAvailable) ?? 0,
      // Null when the buyer named nothing — the sizing mirror short-circuits
      // on that and returns the cash figure untouched.
      //
      // 🔴 AND NULL FOR A COMPANY BUYER (owner rulings Q3 and M1): their one
      // capital answer is the expected equity contribution, and the other-assets
      // section is not shown to them. Assets ticked before the type changed stay
      // in the form (so a switch back loses nothing) and stay off the wire.
      cashEquivalents: companyBuyer ? null : buildCashEquivalents(form),
      // Verbatim, including a typed zero — the blank rule is for the two named-deal
      // figures where zero is not an answer, and this is not one of them.
      reservesAmount: parseMoney(form.reservesAmount),
      reservesBand: form.reservesBand,
      ownershipExperience: form.ownershipExperience,
      managementExperience: form.managementExperience,
      industryFit: form.industryFit,
      involvement: form.involvement,
      ...managementAnswers(form, followUp),
      operatingGeography: {
        mode: form.operatingGeographyMode,
        homeCity: form.homeCity.trim() || null,
        willingToRelocate: form.operatingGeographyMode === "anywhere",
      },
      knockouts: form.knockouts,
      goals: form.goals.trim() || null,
      // Company & sponsor buyers. The type as answered; the company and equity
      // answers ONLY while the type is a company type (the full-time-manager
      // answers above, only for the follow-up actually asked) — a retracted
      // answer stays in the form and off the wire, the same rule as the deal
      // figures.
      // The server applies the same normalisation; this keeps the live meter and
      // the draft honest too.
      buyerType: asBuyerType(form.buyerType),
      acquiringCompanyName: companyBuyer ? form.acquiringCompanyName.trim() || null : null,
      acquiringCompanyTitle: companyBuyer ? form.acquiringCompanyTitle.trim() || null : null,
      acquiringCompanyWebsite: companyBuyer ? form.acquiringCompanyWebsite.trim() || null : null,
      acquiringCompanyAbout: companyBuyer ? form.acquiringCompanyAbout.trim() || null : null,
      equitySource: companyBuyer ? asEquitySource(form.equitySource) : null,
    };
    // `onDealPath` and `followUp` are pure functions of `form`, so they move
    // exactly when form does — listed because the body reads them, not because
    // they can go stale independently.
  }, [form, onDealPath, followUp]);

  // The driving input for the sizing meter: the buyer's TOTAL down-payment
  // capacity. Cash on hand was the wrong question — the engine divides the
  // combined figure, so a buyer with a blank cash field and $100,000 of
  // business equity has answered this step and must see their range.
  //
  // 🔴 AND ON THE INTERNAL ARM CAPACITY IS NOT AN INPUT AT ALL, so this is one
  // condition per arm rather than one condition plus a conjunct. That arm sizes
  // `price = note + down` where the note is the 20-year Maximum Financed Amount
  // on `targetAnnualCashFlow` (prequal-v7) and `down` is `targetDownPayment` —
  // neither of which is
  // `totalDownPaymentCapacity`, which reads cash on hand plus named non-cash
  // assets. Keying both arms on capacity was wrong in BOTH directions:
  //
  //   - an internal buyer who names no capital — the normal case on an arm that
  //     exists because Phoenix requires no down payment from them — saw no
  //     meter at all, ever; and
  //   - one who did name some saw a confident figure the moment they typed it,
  //     sized from a blank cash flow, so the note was zero and the whole
  //     headline was their own money handed back as a qualification. If that
  //     money was under the program minimum,
  //     `internal-buyer-below-program-minimum` DECLINED them instead — live in
  //     the funnel, for not having finished the form.
  //
  // The cash flow alone gates this arm. `targetDownPayment` deliberately does
  // not: zero is a real answer there, so requiring it would restore the first
  // failure in a new place.
  const ready = onInternalPath
    ? (answers.targetAnnualCashFlow ?? 0) > 0
    : totalDownPaymentCapacity(answers) > 0;

  // A capital complaint renders against the cash field itself. It describes a
  // CONDITION, so it stops rendering the moment the condition clears rather
  // than sitting red beneath a field the buyer has just filled in.
  const capitalError =
    stepError?.field === "cash" && !ready ? stepError.message : undefined;

  // Same shape as the capital complaint above: it describes a CONDITION, so it
  // stops rendering the moment the buyer supplies a price rather than sitting
  // red under a field they have just filled in.
  const targetPriceError =
    stepError?.field === "target-price" && nullIfNotPositive(parseMoney(form.targetPurchasePrice)) == null
      ? stepError.message
      : undefined;

  // The internal path's other two complaints, same condition-scoped shape.
  const targetCashFlowError =
    stepError?.field === "target-cash-flow" &&
    nullIfNotPositive(parseMoney(form.targetAnnualCashFlow)) == null
      ? stepError.message
      : undefined;

  const targetBusinessError =
    stepError?.field === "target-business" && form.targetBusinessName.trim() === ""
      ? stepError.message
      : undefined;

  // The designated operator's name — same condition-scoped shape: it stops
  // rendering the moment a name is typed or the answer no longer needs one.
  const operatorNameError =
    stepError?.field === "operator-name" && operatorNameMissing ? stepError.message : undefined;

  // Live sizing — pure client-side indicative math on every answer change
  // (no network; the server engine recomputes authoritatively at finalize).
  //
  // 🔴 A COMPANY TYPE IS PREVIEWED AS A COMPANY BEFORE ITS NAME IS TYPED. The
  // engine's designated-operator path needs the company NAME (one predicate,
  // BuyerAcquirer.IsCompanyBuyer), and the funnel asks the name last, on the
  // review step — so without this a company buyer on the designated-operator
  // path would watch the meter decline them for three steps for a field they
  // have not been shown yet. Finalize refuses a company type without a name, so
  // the preview describes the only outcome a submission can actually have. The
  // placeholder never reaches the wire: `answers` is what is sent.
  const sizingAnswers = useMemo<PrequalBuyerAnswers>(
    () =>
      companyBuyer && !answers.acquiringCompanyName
        ? { ...answers, acquiringCompanyName: "the acquiring company" }
        : answers,
    [answers, companyBuyer],
  );
  const result = useMemo(
    () => (ready ? computeBuyerSizing(sizingAnswers) : null),
    [sizingAnswers, ready],
  );

  // §3.2/§2 — per-step guide map (only the CURRENT step's tip renders),
  // entry-gated on the primary-field predicate, auto-refreshed on a
  // debounce after answer changes. Failure keeps the last good tip.
  const guide = usePrequalGuide({
    steps: BUYER_STEPS,
    stepIndex,
    stepReady: (key) => stepPrimaryFilled(key, form, ready),
    // The guide runs the SERVER engine, so it is handed the same previewed
    // answers as the meter — or it would coach a company buyer about an
    // absentee decline the meter beside it does not show.
    fetchTip: (stepKey) => prequalificationApi.guide({ role: "Buyer", buyer: sizingAnswers, stepKey }),
  });

  const set =
    <K extends keyof BuyerForm>(key: K) =>
    (value: BuyerForm[K]) => {
      guide.onAnswerChanged();
      setForm((f) => ({ ...f, [key]: value }));
    };

  function goTo(index: number) {
    setStepError(null);
    setStepIndex(index);
    setMaxReached((m) => Math.max(m, index));
    guide.onStepEnter(BUYER_STEPS[index].key);
  }

  const identity = {
    firstName: form.firstName,
    lastName: form.lastName,
    email: form.email,
  };

  function advance() {
    const validation = validateStep();
    if (validation) {
      setStepError(validation);
      // The complaint is about the cash field, so put the cursor in it: the
      // message renders down there, and a keyboard visitor is otherwise left
      // hunting for whatever just changed.
      if (validation.field === "cash") cashInputRef.current?.focus();
      if (validation.field === "target-price") targetPriceInputRef.current?.focus();
      if (validation.field === "target-cash-flow") targetCashFlowInputRef.current?.focus();
      if (validation.field === "target-business") targetBusinessInputRef.current?.focus();
      if (validation.field === "operator-name") operatorNameInputRef.current?.focus();
      return;
    }
    const next = stepIndex + 1;
    // The draft is created by the POST behind this action — never by the
    // landing GET, which every mail scanner and link-preview bot fetches
    // unattended the moment an invite is delivered. Fire-and-forget: a save
    // that fails must not hold up somebody's questionnaire.
    draft.persist({ stepIndex: next, answers, identity });
    goTo(next);
  }

  function validateStep(): StepValidation | null {
    switch (BUYER_STEPS[stepIndex].key) {
      case "start": {
        // The fork is this step’s ONE question and the whole reason it is a step.
        // Refused rather than defaulted: it decides which documents get
        // generated, and picking for the buyer is how somebody ends up with a
        // Deal Sheet on a business they never named, or without one on a
        // business they did.
        //
        // 🔴 NARROWED, NOT NULL-CHECKED. `asDocumentPath` is what decides which
        // value reaches the wire, so the gate has to ask IT — a bare
        // `=== null` passes any value that is merely not null, the cards then
        // render nothing selected, Continue advances anyway, and
        // `asDocumentPath` ships null, which the server reads as "not asked" and
        // therefore as "wanted both". That is reachable from a hand-edited
        // sessionStorage blob and from a draft written by an older build, which
        // is exactly what `asDocumentPath`’s own comment promises this gate
        // handles by asking the question again.
        //
        // The complaint has no `field`, so it renders in the page-level slot —
        // which on a step holding one question is directly beneath that
        // question. That was the whole defect when this gate lived on the old
        // combined target step: the same sentence, two screens from the control
        // it was about.
        if (asDocumentPath(form.documentPath) === null) {
          return { message: "Choose what you need from Phoenix to continue." };
        }
        return null;
      }
      case "target": {
        // 🔴 THE GATES RUN IN THE ORDER THE FIELDS ARE READ, and on the internal
        // arm the relationship is the first field on the step — above the
        // business name, above the money — because it is the reason every
        // question beneath it differs. So it is checked first, before the
        // business-name gate it SHARES with the deal arm. Ordering the shared
        // gate first would complain about a field halfway down the page while an
        // unanswered control sat at the top of it.
        //
        // 🔴 NARROWED, for the same reason the fork is: the memo ships what
        // `asInternalRelationship` returns, so a value that is merely non-null
        // would pass this gate, render nothing selected, and reach the server as
        // null — which on the one REQUIRED answer of this path reads as "not
        // asked".
        if (onInternalPath && asInternalRelationship(form.internalRelationship) === null) {
          return { message: "Tell us how you’re connected to the business to continue." };
        }
        // 🔴 REQUIRED ON BOTH NAMED-DEAL ARMS, and asked FIRST on the deal arm.
        // Every
        // figure on this step is a figure ABOUT something; a Deal Sheet whose
        // subject nobody named is a price attached to no business, and the
        // specialist who picks the file up has no way to tell which deal was
        // priced. Validated before the money for the same reason it is asked
        // before it — the focus jump should land on the first thing missing,
        // reading down the page.
        if (statesADeal && form.targetBusinessName.trim() === "") {
          return {
            message: onInternalPath
              ? "Enter the name of the business you’re buying."
              : "Enter the name of the business you’re looking at.",
            field: "target-business",
          };
        }
        // 🔴 REQUIRED ON THE DEAL PATH BECAUSE THE BAND QUESTION IS HIDDEN HERE.
        // This buyer is asked for a price exactly once; if they skip it there is
        // no price answer at all — not a vaguer one — and a "Deal Sheet priced
        // on the business you are looking at" would be priced on nothing.
        if (onDealPath && nullIfNotPositive(parseMoney(form.targetPurchasePrice)) == null) {
          return {
            message: "Enter the asking price, or go back and choose “I’m still looking”.",
            field: "target-price",
          };
        }
        if (onInternalPath) {
          // 🔴 REQUIRED AND POSITIVE — both of them, because on this path they
          // ARE the qualification rather than a document’s pricing. A range sized
          // from a blank cash flow is not a conservative answer, it is a
          // meaningless one, and the finalize boundary refuses the same two.
          if (nullIfNotPositive(parseMoney(form.targetPurchasePrice)) == null) {
            return {
              message: "Enter the asking price for the business.",
              field: "target-price",
            };
          }
          if (nullIfNotPositive(parseMoney(form.targetAnnualCashFlow)) == null) {
            return {
              message:
                "Enter the business’s annual free cash flow — it is what qualifies this purchase.",
              field: "target-cash-flow",
            };
          }
        }
        return null;
      }
      case "capital": {
        // 🔴 NOTHING ON THIS STEP GATES AN INTERNAL BUYER. The premise of the
        // path is a buyer who is not being asked for a down payment — a son
        // buying his father’s company, a manager buying from the owner — so
        // turning them away for naming no capital would refuse exactly the
        // person the path exists to qualify, on a step whose figures size
        // nothing for them. The questions are still ASKED (the answers are real
        // context for a specialist, and a buyer who does have capital should be
        // able to say so); they simply do not stop anybody.
        if (onInternalPath) return null;
        // FIRST — the other-assets section as a whole stays OPTIONAL, but
        // ticking a category is a promise to say what it is worth, and a ticked
        // category with no figure would silently drop out of the payload.
        //
        // Checked AHEAD of the total below because it is the more specific
        // complaint: "you left this box empty" is something the buyer can act
        // on, where "you have named no capital" reads as nonsense to somebody
        // who has just ticked the box they believe answers it.
        //
        // Not for a company buyer: the section is not shown to them (Q3), so a
        // box ticked before the type changed must not block a step that no
        // longer displays it.
        const unpriced = companyBuyer
          ? undefined
          : OTHER_ASSET_CATEGORIES.find(
              (key) => form.otherAssetSelected[key] && namedAssetAmount(form, key) === null,
            );
        if (unpriced) {
          // Named as the checkbox is labelled — the published label, or the
          // default when the published one is blank, since a complaint has to
          // say which box it means.
          const block = otherAssetBlock(unpriced);
          const label = hasText(q(block)) ? q(block) : FUNNEL_QUESTION_DEFAULTS[block];
          return { message: `Enter an amount for “${label}”, or untick it.` };
        }
        // THEN — the step needs CAPITAL, from anywhere. `ready` is the very
        // figure the sizing engine divides (a blank cash field serializes as 0;
        // an unticked category is absent, never a zero), so the gate and the
        // meter cannot disagree about whether this buyer has been sized.
        return ready ? null : { message: NO_CAPITAL_ERROR, field: "cash" };
      }
      case "background": {
        // Every control here ships with a default — EXCEPT the designated
        // operator's name (D4, FT): a principal of the acquiring company,
        // existing management or another person an individual designates
        // satisfies the full-time-management screen only when the buyer says
        // who. Optional for a general manager still to be hired. The follow-up
        // itself stays optional — a non-operator who answers nothing is the
        // engine's absentee decline, the same as today, not a form error.
        if (operatorNameMissing) {
          return { message: OPERATOR_NAME_MISSING, field: "operator-name" };
        }
        return null;
      }
      case "eligibility":
      case "review":
        // UNGATED, and each for its own reason rather than by falling through a
        // shared default:
        //
        //  - **eligibility**: an unanswered knockout is INERT by design — the
        //    engine reads "no" as a soft decline and silence as nothing at all,
        //    so demanding an answer would be demanding a disclosure. A buyer who
        //    skips the lot is allowed to.
        //  - **review**: goals is optional, and the contact fields and the
        //    consent box are validated by `FinalizeSection` at submit, where the
        //    request is built. A second gate here would either duplicate those
        //    rules or drift from them.
        return null;
      default:
        // 🔴 UNREACHABLE, AND THE POINT IS THAT IT SAYS SO. Every key in
        // BUYER_STEPS has an arm above. A step added without one falls in here
        // and is UNGATED — silently, with nothing thrown and nothing logged, so
        // a required answer added to it would simply never be required. If you
        // add a step, add an arm, even when the arm returns null.
        return null;
    }
  }

  function handleFinalized(res: PrequalFinalizeResult) {
    setFinalized(res);
    // Persist synchronously — the navigation unmounts this page before the
    // save effect could run.
    saveFunnelState(
      PREQUAL_BUYER_STORAGE_KEY,
      { form, stepIndex, maxReached, finalized: res },
      scope,
    );
    // The `/buy` hand-off has done its whole job by now. Dropping it here keeps
    // contact details from sitting in storage for the life of the tab, and stops
    // them prefilling whoever starts a funnel in this tab next. Synchronous for
    // the same reason the save above is.
    forgetBuyPrefill();
    navigate(`${base}/buyer/result`);
  }

  // §7 — the only way funnel state is cleared.
  function startOver() {
    clearFunnelState(PREQUAL_BUYER_STORAGE_KEY);
    // Start over means start over. Without this the blob outlives the run it was
    // written for: a reload would prefill the "new" application from the old
    // one, and the phone field would go on describing a number just cleared.
    forgetBuyPrefill();
    setForm(initialForm);
    setStepIndex(0);
    setMaxReached(0);
    setFinalized(null);
    setStepError(null);
    setCompletedGateDismissed(true);
    guide.reset();
  }

  const stepKey = BUYER_STEPS[stepIndex].key;
  // Built from the RESOLVED copy, not the module constant — so a published edit
  // changes the step headings without a deploy.
  const meta = buyerStepMeta(marketing, companyBuyer)[stepKey];

  // Prefill contact from a LIVE invite. The invite named this person at send
  // time, so making them retype it is pure friction. Applied once, and only
  // into fields that are still blank — a restored session, or anything the
  // visitor has already typed, always wins over the prefill.
  const invitePrefilled = useRef(false);
  useEffect(() => {
    const live = invite.invite;
    if (invitePrefilled.current || live === null || !live.isLive) return;
    invitePrefilled.current = true;
    setForm((current) => ({
      ...current,
      firstName: current.firstName || (live.firstName ?? ""),
      lastName: current.lastName || (live.lastName ?? ""),
      email: current.email || (live.email ?? ""),
    }));
  }, [invite.invite]);

  // The same treatment for a visitor arriving from `/buy`, which asked for all
  // three of these and required every one of them. Without this the funnel's
  // closing block asks for them again, AFTER everything else has been answered.
  //
  // Identical shape to the invite prefill above, and identically blank-only, so
  // the two compose without either overwriting a restored session or anything
  // the visitor has typed. `/buy` collects no last name, so that field is left
  // alone — it is the one genuinely new thing the closing block asks for.
  const buyPrefilled = useRef(false);
  useEffect(() => {
    if (buyPrefilled.current || buyPrefill === null) return;
    buyPrefilled.current = true;
    setForm((current) => ({
      ...current,
      firstName: current.firstName || buyPrefill.firstName,
      email: current.email || buyPrefill.email,
      phone: current.phone || buyPrefill.phone,
    }));
  }, [buyPrefill]);

  // A dead invite link is a friendly dead end, never the funnel and never an
  // error page — somebody was invited here by a person they know, so the useful
  // thing is to name who to ask. Rendered only once the lookup has SETTLED
  // (isDead stays false while pending), so a slow resolve never flashes this.
  if (invite.isDead && invite.invite !== null) {
    return (
      <PartnerThemeProvider brand={brand}>
        <PortalShell pageTitle="Buyer prequalification" wide publicFunnel partner={brand} brandPending={brandPending} compact={assisted}>
          {notFound && !assisted && <PartnerFallbackNotice />}
          <PrequalInviteExpiredCard invite={invite.invite} role="Buyer" />
        </PortalShell>
      </PartnerThemeProvider>
    );
  }

  // The completed-application fork. Deliberately a full stop rather than a
  // banner over the form: the one thing that must not happen is somebody
  // typing into what turns out to be a finished application.
  if (completedOnMount !== null && !completedGateDismissed) {
    return (
      <PartnerThemeProvider brand={brand}>
        <PortalShell pageTitle="Buyer prequalification" wide publicFunnel partner={brand} brandPending={brandPending} compact={assisted}>
          {notFound && !assisted && <PartnerFallbackNotice />}
          <CompletedApplicationCard
            role="Buyer"
            finalized={completedOnMount}
            onView={() => navigate(`${base}/buyer/result`)}
            onAdjust={() => setCompletedGateDismissed(true)}
            onStartNew={startOver}
          />
        </PortalShell>
      </PartnerThemeProvider>
    );
  }

  return (
    <PartnerThemeProvider brand={brand}>
    <PortalShell pageTitle="Buyer prequalification" wide publicFunnel partner={brand} brandPending={brandPending} compact={assisted}>
      {notFound && !assisted && <PartnerFallbackNotice />}
      {/* ASSISTED trims this to the title. The overline names the Phoenix
          PROGRAM and the subhead recites its terms — both are pitch, written
          for a stranger deciding whether to start. The operator has read them
          twenty times, and the applicant beside them was pitched in person. */}
      <div className="mb-space-4">
        {assisted || !hasText(marketing.programOverline) ? null : (
          <p
            className="text-label uppercase tracking-widest sm:tracking-[0.24em] text-gold-deep mt-0 mb-space-2"
            data-testid="prequal-program-overline"
          >
            {marketing.programOverline}
          </p>
        )}
        <h1 className="text-h3 sm:text-h2 font-display uppercase tracking-wide text-ink m-0">
          Buyer prequalification
        </h1>
        <div className="h-[3px] w-16 bg-gold mt-space-2" aria-hidden="true" />
        {assisted ? (
          // Step one only. "You can stop and come back" is worth knowing while
          // the operator is deciding whether to keep going; above every step it
          // is noise by the third, and on the finalize step it arrives after
          // the window in which it was useful.
          stepIndex === 0 && (
            <p
              className="text-small text-ink-mid mt-space-3 mb-0"
              data-testid="assist-save-note"
            >
              {ASSIST_SAVE_NOTE}
            </p>
          )
        ) : (
          <p className="text-small text-ink-mid mt-space-3 mb-0">{PROGRAM_TERMS_SUBHEAD}</p>
        )}
      </div>

      <FunnelProgress
        steps={BUYER_STEPS}
        currentIndex={stepIndex}
        maxReached={maxReached}
        onSelect={goTo}
      />
      <p className="text-right mt-space-1 mb-0">
        <button
          type="button"
          onClick={startOver}
          data-testid="prequal-start-over"
          className="bg-transparent border-0 p-0 text-caption text-ink-faint hover:text-gold-deep cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-sm"
        >
          Start over
        </button>
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_23.125rem] gap-space-5 mt-space-4">
        <div className="min-w-0 flex flex-col gap-space-4">
          <QuestionCard
            stepNumber={stepIndex + 1}
            section={BUYER_STEPS[stepIndex].label}
            title={meta?.title ?? BUYER_STEPS[stepIndex].label}
            hint={meta?.hint}
          >
            {/* STEP 1 — the fork, and NOTHING else. Three cards, each naming
                what Phoenix hands back for it. It had to become its own step:
                the question decides what every question after it asks, and
                while it sat at the top of a seven-field step it was silently
                required — a buyer answered everything, pressed Continue, and met
                the complaint about the FIRST control rendered below the LAST. */}
            {stepKey === "start" && (
              <DocumentPathCards
                read={q}
                value={form.documentPath}
                onChange={(v) => {
                  guide.onAnswerChanged();
                  setForm((f) => ({ ...f, documentPath: v }));
                }}
              />
            )}

            {stepKey === "target" && (
              <div className="flex flex-col gap-space-3">
                {/* "Who is buying?" (D1) — FIRST on this step, on every arm,
                    because the capital label and the operator follow-up depend
                    on it. Optional: unanswered is an individual, as every
                    application before it was. Not on the start step, which asks
                    the fork and nothing else. */}
                <ChoiceField
                  label={marketing.questionBuyerType}
                  options={[
                    { value: "Individual", label: marketing.optionBuyerTypeIndividual },
                    { value: "CompanyOwnedOrLed", label: marketing.optionBuyerTypeCompany },
                    { value: "InvestmentGroupOrFund", label: marketing.optionBuyerTypeFund },
                  ]}
                  value={asBuyerType(form.buyerType)}
                  onChange={(v) => {
                    guide.onAnswerChanged();
                    // A company and an individual are asked different
                    // full-time-manager questions; the stale answer goes.
                    setForm((f) => withFollowUpsFor(f, { ...f, buyerType: v }));
                  }}
                  testIdPrefix="prequal-buyer-type"
                />
                {/* 🔴 THE ARMS DIFFER IN ORDER, NOT ONLY IN CONTENT, and the order
                    is the argument. After "Who is buying?" (above, the same on
                    every arm), on the INTERNAL arm the relationship comes
                    next — above the money — because it is the reason every
                    question beneath it differs, and because it is the fact the
                    letter names and staff later verify. On the DEAL arm the
                    business name comes next, because the figures under it are
                    figures ABOUT something. The letter-only arm opens on the
                    category, which is all that buyer has. */}
                {onInternalPath && (
                  <>
                    {/* The one REQUIRED answer of this path, and the one staff
                        later verify. No `onAdvance` — the figures below it
                        still have to be given. */}
                    <ChoiceField
                      label={q("question-buyer-internal-relationship")}
                      options={optionsFromCopy(
                        INTERNAL_RELATIONSHIP_OPTIONS,
                        "option-buyer-internal-relationship",
                        q,
                      )}
                      value={form.internalRelationship}
                      onChange={set("internalRelationship")}
                      testIdPrefix="prequal-internal-relationship"
                    />
                    {/* Asked of an employee or manager and of nobody else.
                        🔴 NO MINIMUM — it gates nothing on either tier; it is
                        context for the specialist who picks up the file. */}
                    {asInternalRelationship(form.internalRelationship) ===
                      INTERNAL_RELATIONSHIP_WITH_TENURE && (
                      <FormField
                        label={textOrUndefined(q("label-buyer-internal-years"))}
                        helperText={textOrUndefined(q("helper-buyer-internal-years"))}
                      >
                        <Input
                          type="number"
                          min={0}
                          inputMode="numeric"
                          value={form.internalYearsWithBusiness}
                          onChange={(e) => set("internalYearsWithBusiness")(e.target.value)}
                          placeholder="e.g. 7"
                          data-testid="prequal-internal-years"
                        />
                      </FormField>
                    )}
                  </>
                )}

                {/* Three arms, three questions — see the copy constants
                    (TARGET_BUSINESS_*) for why. REQUIRED on both named-deal arms
                    and optional on the letter arm, where the buyer may genuinely
                    be shopping a category. */}
                <FormField
                  label={textOrUndefined(
                    q(
                      onInternalPath
                        ? "label-buyer-target-business-internal"
                        : onDealPath
                          ? "label-buyer-target-business-deal"
                          : "label-buyer-target-business-letter-only",
                    ),
                  )}
                  helperText={textOrUndefined(
                    q(
                      onInternalPath
                        ? "helper-buyer-target-business-internal"
                        : onDealPath
                          ? "helper-buyer-target-business-deal"
                          : "helper-buyer-target-business-letter-only",
                    ),
                  )}
                  error={targetBusinessError}
                >
                  <Input
                    value={form.targetBusinessName}
                    onChange={(e) => set("targetBusinessName")(e.target.value)}
                    placeholder="e.g. Valley Plumbing Co., or a listing link"
                    data-testid="prequal-buyer-target-business"
                    ref={targetBusinessInputRef}
                  />
                </FormField>

                {/* 🔴 THE HELPER IS FORKED AND LIVES IN prequalContent. It used to
                    be an inline literal telling an INTERNAL buyer that "the next
                    question is for that", on the one arm where that question
                    sits ABOVE this field and is already answered. */}
                <FormField
                  label={INDUSTRY_QUESTION}
                  helperText={
                    onInternalPath
                      ? INDUSTRY_HELPER_INTERNAL
                      : onDealPath
                        ? INDUSTRY_HELPER_DEAL
                        : INDUSTRY_HELPER_LETTER_ONLY
                  }
                >
                  <Input
                    value={form.targetIndustry}
                    onChange={(e) => set("targetIndustry")(e.target.value)}
                    placeholder={INDUSTRY_PLACEHOLDER}
                    data-testid="prequal-buyer-industry"
                  />
                </FormField>

                <FormField label={HOME_CITY_LABEL} helperText={HOME_CITY_HELPER}>
                  <Input
                    value={form.homeCity}
                    onChange={(e) => set("homeCity")(e.target.value)}
                    placeholder={HOME_CITY_PLACEHOLDER}
                    data-testid="prequal-buyer-home-city"
                  />
                </FormField>

                {/* 🔴 The band question and the deal fields are the two arms
                    of the same question, so exactly one renders. Asking a buyer
                    who has just typed $2,750,000 to also pick a range is asking
                    twice and inviting two different answers; the band they
                    would have chosen is derived from the price instead (see
                    `priceBandForAmount`), so nothing downstream loses it. */}
                {!statesADeal && (
                  <ChoiceField
                    label={q("question-buyer-price-band")}
                    options={PRICE_BANDS}
                    value={form.priceBand}
                    onChange={set("priceBand")}
                    onAdvance={advance}
                    testIdPrefix="prequal-price-band"
                  />
                )}

                {onInternalPath && (
                  /* A COUNT and no names: naming co-buyers would put unverified
                     people on paperwork a counterparty reads. */
                  <FormField
                    label={textOrUndefined(q("label-buyer-internal-co-buyers"))}
                    helperText={textOrUndefined(q("helper-buyer-internal-co-buyers"))}
                  >
                    <Input
                      type="number"
                      min={0}
                      inputMode="numeric"
                      value={form.internalCoBuyerCount}
                      onChange={(e) => set("internalCoBuyerCount")(e.target.value)}
                      placeholder="e.g. 1"
                      data-testid="prequal-internal-co-buyers"
                    />
                  </FormField>
                )}

                {statesADeal && (
                  <div className="flex flex-col gap-space-3">
                    <CurrencyField
                      label={q("label-buyer-asking-price")}
                      required
                      value={form.targetPurchasePrice}
                      onChange={set("targetPurchasePrice")}
                      helperText={q(
                        onInternalPath
                          ? "helper-buyer-asking-price-internal"
                          : "helper-buyer-asking-price-deal",
                      )}
                      error={targetPriceError}
                      inputRef={targetPriceInputRef}
                      testId="prequal-buyer-target-price"
                    />
                    <CurrencyField
                      label={q(
                        onInternalPath ? "label-buyer-cash-flow-internal" : "label-buyer-cash-flow-deal",
                      )}
                      required={onInternalPath}
                      value={form.targetAnnualCashFlow}
                      onChange={set("targetAnnualCashFlow")}
                      helperText={q(
                        onInternalPath ? "helper-buyer-free-cash-flow-internal" : "helper-buyer-free-cash-flow-deal",
                      )}
                      error={targetCashFlowError}
                      inputRef={targetCashFlowInputRef}
                      testId="prequal-buyer-target-cash-flow"
                    />
                    {/* 🔴 "Cash at closing (optional)" USED TO SIT HERE AND IS
                        RETIRED. Phoenix asks no down payment and sets no minimum,
                        so the field invited a buyer to nominate a figure against
                        a requirement that does not exist — and an empty box
                        beneath two required ones reads as an omission rather
                        than as the answer it was. The capital step already asks
                        what they have; the REVIEW step shows the assumption back
                        instead of asking again. `targetDownPayment` survives on
                        the form type, in `initialForm` and in `hydrateBuyerForm`
                        so a resumed HISTORICAL draft still round-trips its
                        stored figure — it is simply never asked for again, and
                        the answers memo sends null. */}
                    <FormField
                      label={textOrUndefined(q("label-buyer-close-date"))}
                      helperText={textOrUndefined(q("helper-buyer-close-date"))}
                    >
                      <Input
                        type="date"
                        value={form.targetPurchaseDate}
                        onChange={(e) => set("targetPurchaseDate")(e.target.value)}
                        data-testid="prequal-buyer-target-date"
                      />
                    </FormField>
                  </div>
                )}
              </div>
            )}


            {stepKey === "capital" && (
              <div className="flex flex-col gap-space-3">
                {/* A company buyer's one capital answer is the acquiring
                    company's expected equity contribution (D3, Q3) — the same
                    field, relabelled, and no other-assets section beneath it. */}
                <CurrencyField
                  label={companyBuyer ? marketing.labelBuyerCapitalCompany : q("label-buyer-cash")}
                  required
                  value={form.cashAvailable}
                  onChange={set("cashAvailable")}
                  helperText={companyBuyer ? marketing.helperBuyerCapitalCompany : q("helper-buyer-cash-note-holder")}
                  error={capitalError}
                  inputRef={cashInputRef}
                  testId="prequal-buyer-cash"
                />
                {companyBuyer && (
                  <ChoiceField
                    label={marketing.questionBuyerEquitySource}
                    options={[
                      { value: "CompanyFundsOnHand", label: marketing.optionBuyerEquitySourceOnHand },
                      { value: "CommittedByInvestors", label: marketing.optionBuyerEquitySourceCommitted },
                      { value: "StillBeingRaised", label: marketing.optionBuyerEquitySourceRaising },
                    ]}
                    value={asEquitySource(form.equitySource)}
                    onChange={set("equitySource")}
                    testIdPrefix="prequal-equity-source"
                  />
                )}
                {!companyBuyer && (
                <OtherAssetsSection
                  read={q}
                  selected={form.otherAssetSelected}
                  amounts={form.otherAssetAmounts}
                  note={form.otherAssetsNote}
                  onToggle={(key, checked) => {
                    guide.onAnswerChanged();
                    setForm((f) => ({
                      ...f,
                      otherAssetSelected: { ...f.otherAssetSelected, [key]: checked },
                      // Unticking clears the (now-hidden) figure, so a stale
                      // amount can never come back with the box — the AAMA
                      // follow-up on the fit-check step works the same way.
                      otherAssetAmounts: checked
                        ? f.otherAssetAmounts
                        : { ...f.otherAssetAmounts, [key]: "" },
                    }));
                  }}
                  onAmountChange={(key, raw) => {
                    guide.onAnswerChanged();
                    setForm((f) => ({
                      ...f,
                      otherAssetAmounts: { ...f.otherAssetAmounts, [key]: raw },
                    }));
                  }}
                  onNoteChange={set("otherAssetsNote")}
                />
                )}
                <CurrencyField
                  label={q("label-buyer-reserves")}
                  value={form.reservesAmount}
                  onChange={set("reservesAmount")}
                  helperText={q("helper-buyer-reserves")}
                  testId="prequal-reserves-amount"
                />
                {/* No `onAdvance` — and read what it actually did before adding
                    it back. It is NOT click-to-advance: `ChoiceButtons` wires it
                    to one key path, Enter on a chip that is ALREADY selected. So
                    a keyboard user who picked a band and pressed Enter left the
                    step, and any question added BELOW the band was skipped for
                    them, silently. That is why the amount is above it and why
                    this one chip differs from the seven that keep theirs: this
                    is the step that grew a second question. The step advances on
                    Continue, like every other one.
                    Pinned by BuyerPrequalifyPage.test.tsx, which drives Enter —
                    a click-based version of that test was green with the prop
                    present AND absent. */}
                <ChoiceField
                  label={q("question-buyer-reserves-months")}
                  options={optionsFromCopy(RESERVES_BANDS, "option-buyer-reserves", q)}
                  value={form.reservesBand}
                  onChange={set("reservesBand")}
                  testIdPrefix="prequal-reserves"
                />
              </div>
            )}

            {/* STEP 4 — BACKGROUND. The three ladders plus the two OPERATOR
                questions that used to be stranded on the fit check. Neither
                "how will you be involved" nor "where will you operate" is an
                eligibility question: both describe the person, which is what
                this step is, and the step they were on is now the knockouts and
                nothing else. */}
            {stepKey === "background" && (
              <div className="flex flex-col gap-space-4">
                <ChoiceField
                  label={q("question-buyer-ownership")}
                  options={optionsFromCopy(OWNERSHIP_LADDER, "option-buyer-ownership", q)}
                  value={form.ownershipExperience}
                  onChange={set("ownershipExperience")}
                  testIdPrefix="prequal-ownership"
                />
                <ChoiceField
                  label={q("question-buyer-management")}
                  options={optionsFromCopy(MANAGEMENT_LADDER, "option-buyer-management", q)}
                  value={form.managementExperience}
                  onChange={set("managementExperience")}
                  testIdPrefix="prequal-management"
                />
                <ChoiceField
                  label={q("question-buyer-industry-fit")}
                  options={optionsFromCopy(INDUSTRY_LADDER, "option-buyer-industry-fit", q)}
                  value={form.industryFit}
                  onChange={set("industryFit")}
                  testIdPrefix="prequal-fit"
                />

                {/* ⚠️ NO `onAdvance` ON ANY OF THE THREE LADDERS ANY MORE, and it
                    was removed rather than carried over. `ChoiceButtons` wires
                    that prop to ONE key path — Enter on a chip that is ALREADY
                    selected — so a keyboard visitor who picked an answer and
                    pressed Enter left the step. That was survivable while these
                    three were the whole step; this step now has three questions
                    BELOW them, and Enter on the first would skip all three
                    silently. Exactly the reserves-pair defect, which is pinned
                    by BuyerPrequalifyPage.test.tsx and argued at the reserves
                    band above. The step advances on Continue. */}

                <ChoiceField
                  label={q("question-buyer-involvement")}
                  options={optionsFromCopy(INVOLVEMENT_OPTIONS, "option-buyer-involvement", q)}
                  value={form.involvement}
                  onChange={(v) => {
                    guide.onAnswerChanged();
                    // A different follow-up clears the answers the old one held;
                    // becoming the operator clears them all.
                    setForm((f) => withFollowUpsFor(f, { ...f, involvement: v }));
                  }}
                  testIdPrefix="prequal-involvement"
                />

                {/* FT — an individual overseer: the requirement, then who will
                    manage the business full time, AAMA among the five choices. */}
                {followUp === "overseer" && (
                  <>
                    {hasText(q("requirement-buyer-managed-full-time")) && (
                      <p className="text-small text-ink-mid m-0" data-testid="prequal-management-requirement">
                        {q("requirement-buyer-managed-full-time")}
                      </p>
                    )}
                    <ChoiceField
                      label={q("question-buyer-full-time-manager")}
                      options={[
                        { value: "HiredGeneralManager", label: q("option-buyer-manager-gm-individual") },
                        { value: "ExistingManagement", label: q("option-buyer-manager-existing-individual") },
                        { value: "DesignatedPerson", label: q("option-buyer-manager-designated-person") },
                        { value: "aama", label: q("option-buyer-manager-aama") },
                        { value: "undecided", label: q("option-buyer-manager-undecided") },
                      ]}
                      value={managerChoice}
                      onChange={(v) => {
                        guide.onAnswerChanged();
                        setForm((f) => chooseManager(f, v));
                      }}
                      testIdPrefix="prequal-manager"
                    />
                  </>
                )}

                {/* D4 — a company buyer who will not run the business says who
                    will manage it full time: a designated operator. Above AAMA,
                    which stays available (and the designated operator wins when
                    both are answered, ruling Q5). */}
                {followUp === "company" && (
                  <ChoiceField
                    label={q("question-buyer-full-time-manager")}
                    helperText={q("helper-buyer-full-time-manager")}
                    options={[
                      { value: "CompanyPrincipal", label: marketing.optionBuyerOperatorPrincipal },
                      { value: "HiredGeneralManager", label: marketing.optionBuyerOperatorGeneralManager },
                      {
                        value: "ExistingManagement",
                        label: marketing.optionBuyerOperatorExistingManagement,
                      },
                    ]}
                    value={operatorArrangement}
                    onChange={set("operatorArrangement")}
                    testIdPrefix="prequal-operator"
                  />
                )}

                {operatorArrangement !== null && (
                  <FormField
                    label={textOrUndefined(
                      operatorArrangement === "HiredGeneralManager"
                        ? marketing.labelBuyerOperatorNameOptional
                        : marketing.labelBuyerOperatorName,
                    )}
                    required={NAME_REQUIRED.includes(operatorArrangement)}
                    error={operatorNameError}
                  >
                    <Input
                      ref={operatorNameInputRef}
                      value={form.operatorName}
                      maxLength={COMPANY_FIELD_LIMITS.operatorName}
                      onChange={(e) => set("operatorName")(e.target.value)}
                      data-testid="prequal-operator-name"
                    />
                  </FormField>
                )}

                {/* D2 — a hands-off individual meets the requirement only through
                    AAMA, so the requirement is said that way and nothing else is
                    asked. A company asks the same AAMA question after its own. */}
                {followUp === "hands-off" &&
                  hasText(q("requirement-buyer-managed-full-time-hands-off")) && (
                    <p className="text-small text-ink-mid m-0" data-testid="prequal-management-requirement">
                      {q("requirement-buyer-managed-full-time-hands-off")}
                    </p>
                  )}
                {(followUp === "company" || followUp === "hands-off") && (
                  <ChoiceField
                    label={q("question-buyer-aama")}
                    options={[
                      { value: "yes", label: q("option-buyer-aama-yes") },
                      { value: "no", label: q("option-buyer-aama-neither") },
                    ]}
                    value={form.aamaAnswer || null}
                    onChange={(v) => {
                      guide.onAnswerChanged();
                      // AAMA buyers auto-widen — the manager they install runs
                      // the business, not the buyer. A default they can change.
                      setForm((f) => ({
                        ...f,
                        aamaAnswer: v,
                        operatingGeographyMode:
                          v === "yes" ? "anywhere" : f.operatingGeographyMode,
                      }));
                    }}
                    testIdPrefix="prequal-aama"
                  />
                )}

                <ChoiceField
                  label={q("question-buyer-operating-geography")}
                  options={optionsFromCopy(GEOGRAPHY_OPTIONS, "option-buyer-geography", q)}
                  value={form.operatingGeographyMode}
                  onChange={(v) => {
                    guide.onAnswerChanged();
                    setForm((f) => ({ ...f, operatingGeographyMode: v }));
                  }}
                  testIdPrefix="prequal-geography"
                />
              </div>
            )}

            {/* STEP 5 — ELIGIBILITY: the seven knockouts, alone. An unanswered
                one is INERT by design, so nothing here gates. */}
            {stepKey === "eligibility" && (
              <KnockoutChecklist
                questions={BUYER_KNOCKOUTS}
                copy={q}
                answers={form.knockouts}
                onAnswer={(key, value) => {
                  guide.onAnswerChanged();
                  setForm((f) => ({ ...f, knockouts: { ...f.knockouts, [key]: value } }));
                }}
              />
            )}

            {/* STEP 6 — REVIEW & SEND: what we were told, then goals, then who
                the documents are made out to. The recap is READ-ONLY; the way
                to change a figure is the stepper or Back, which is why nothing
                here is an input. */}
            {stepKey === "review" && (
              <div className="flex flex-col gap-space-4">
                {statesADeal && (
                  <DealRecap
                    businessName={form.targetBusinessName}
                    industry={form.targetIndustry}
                    price={form.targetPurchasePrice}
                    cashFlow={form.targetAnnualCashFlow}
                    closeDate={form.targetPurchaseDate}
                    internal={onInternalPath}
                  />
                )}
                <CashAtClosingAssumption cash={statedCashAtClosing(answers)} />
                {/* The `funnel-copy` key that used to title the goals STEP now
                    labels the goals FIELD. Still resolved, still rendered, still
                    editable without a deploy — and it cannot be dropped, because
                    its key is pinned against a fixture shared with the .NET test
                    project. */}
                <FormField
                  label={textOrUndefined(marketing.stepTitleBuyerGoals)}
                  helperText={textOrUndefined(q("helper-buyer-goals"))}
                >
                  <Textarea
                    rows={4}
                    value={form.goals}
                    onChange={(e) => set("goals")(e.target.value)}
                    data-testid="prequal-buyer-goals"
                  />
                </FormField>

                <FinalizeSection
                  role="Buyer"
                  result={result}
                  // `/buy` requires a valid number AND SMS consent on the same
                  // step, so a phone carried across means both were given —
                  // which is what makes "Optional" the wrong word here.
                  phoneAlreadyGiven={buyPrefill?.hasPhone ?? false}
                  contact={{
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email,
                    phone: form.phone,
                  }}
                  onContactChange={(patch) => {
                    guide.onAnswerChanged();
                    setForm((f) => ({ ...f, ...patch }));
                  }}
                  buildRequest={(extra) => ({
                    role: "Buyer",
                    buyer: answers,
                    firstName: form.firstName.trim(),
                    lastName: form.lastName.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim() || undefined,
                    consentAccepted: extra.consentAccepted,
                    website: extra.website,
                    // The ROUTE slug, never `brand?.slug`: a buyer team's site
                    // resolves to NO brand (it renders natively — D2a), and the
                    // server resolves the slug itself, degrading an unknown one
                    // to the native funnel exactly as the draft path already
                    // does. Sending the resolved brand's slug dropped every
                    // team-site completion's attribution (review F1).
                    partnerSlug: partnerSlug ?? undefined,
                    inviteToken: invite.token ?? undefined,
                    // Attaches this completion to the BuyerLead /buy already
                    // created for them. The server ignores it when it cannot be
                    // honoured, exactly as it ignores a dead invite token.
                    buyLandingToken: buyLandingToken ?? undefined,
                    // Credits this completion to the advertisement the /buy page
                    // credited. The server ignores it when it cannot be honoured.
                    advertisementToken: advertisementToken ?? undefined,
                  })}
                  // Routed through the adapter so a draft is CONSUMED by the
                  // submission it produced, in the same transaction. Submitting
                  // around it would leave the finished applicant sitting on
                  // somebody's abandoned-draft follow-up list.
                  submit={draft.finalize}
                  onFinalized={handleFinalized}
                  beforeSubmit={finalizeSlot}
                  // Company & sponsor buyers (D2): the acquiring company beside
                  // the person's name, and its client-side check. Absent for an
                  // individual, which renders exactly as before.
                  contactExtras={
                    companyBuyer ? (
                      <CompanyDetailsFields
                        form={form}
                        marketing={marketing}
                        onChange={(patch) => {
                          guide.onAnswerChanged();
                          setForm((f) => ({ ...f, ...patch }));
                        }}
                      />
                    ) : undefined
                  }
                  validateExtras={companyBuyer ? () => validateCompanyDetails(form) : undefined}
                />
              </div>
            )}
          </QuestionCard>

          {/* The page-level slot, for complaints about the step as a whole. A
              field-scoped one (`field` set) renders against its own control
              instead — see `capitalError`. */}
          {stepError && stepError.field === undefined && (
            <p className="text-small text-status-error m-0" role="alert">
              {stepError.message}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-space-3">
            {stepIndex > 0 && (
              <Button type="button" variant="secondary" onClick={() => goTo(stepIndex - 1)}>
                ← Back
              </Button>
            )}
            {stepKey !== "review" && (
              <Button
                type="button"
                onClick={advance}
                className="hover:bg-gold-deep hover:text-white-pure"
                data-testid="prequal-next"
              >
                Continue →
              </Button>
            )}
          </div>
        </div>

        <aside className="flex flex-col gap-space-4">
          <SizingMeter
            role="Buyer"
            result={result}
            ready={ready}
            internalPurchase={onInternalPath}
          />
          <PrequalSaveProgressCard
            mode={adapter.mode}
            invited={invite.token !== null}
            showIdentity={stepKey !== "review"}
            identity={identity}
            onIdentityChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            onSave={() => draft.persist({ stepIndex, answers, identity })}
            session={draft}
          />
          <ProgramGlanceCard role="Buyer" />
          <GuidePanel
            text={guide.guides[stepKey] ?? null}
            onRefresh={guide.refreshNow}
            refreshing={guide.refreshing}
          />
          {hasText(administratorLine) && (
            <p className="text-caption text-ink-faint m-0" data-testid="prequal-administrator-line">{administratorLine}</p>
          )}
          {hasText(indicativeLabel) && (
            <p className="text-caption text-ink-faint m-0" data-testid="prequal-indicative-label">{indicativeLabel}</p>
          )}
        </aside>
      </div>
    </PortalShell>
    </PartnerThemeProvider>
  );
}

/**
 * The opening fork, as three cards.
 *
 * Deliberately NOT `ChoiceField`. That renders a row of pills, which is right
 * for "roughly what price range?" and wrong for the one question on this step:
 * each arm needs to say who the buyer is, what they want done, and what Phoenix
 * hands back, and a pill holds a label. The three lines are the point — a buyer
 * arriving here does not yet know what a "Deal Sheet" is, so the choice has to
 * be answerable from its OUTCOME.
 *
 * 🔴 THE TESTID AND `aria-pressed` CONTRACT IS PRESERVED EXACTLY, and that is
 * not incidental tidiness. `prequal-document-path-{value}` with `aria-pressed`
 * is what the funnel suites drive and what `prequalSessionScope.test.tsx` — owned
 * elsewhere — asserts a resumed draft restores. A card that dropped either would
 * break a resume pin for a reason that has nothing to do with resumes.
 *
 * The group takes its accessible name from the visible question through
 * `aria-labelledby`, not a `<label for>`: these are buttons in a div, and a
 * label aimed at a div names nothing.
 */
function DocumentPathCards({
  read,
  value,
  onChange,
}: {
  /** The cards' words are Document Language blocks (D7): `option-buyer-path-<path>`,
   *  `…-body`, `…-produces`. The values stay coded. */
  read: QuestionCopy;
  value: string | null;
  onChange: (value: string) => void;
}) {
  const headingId = useId();
  return (
    <div className="flex flex-col gap-space-3">
      <p id={headingId} className="text-small text-ink m-0">
        What do you need from Phoenix today?
      </p>
      <div
        role="group"
        aria-labelledby={headingId}
        aria-label="What do you need from Phoenix today?"
        className="grid grid-cols-1 gap-space-3"
      >
        {DOCUMENT_PATH_CARDS.map((card) => {
          const selected = value === card.value;
          const block = `option-buyer-path-${blockSlug(card.value)}`;
          const label = read(questionBlock(block));
          const body = read(questionBlock(`${block}-body`));
          const produces = read(questionBlock(`${block}-produces`));
          return (
            <button
              key={card.value}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(card.value)}
              data-testid={`prequal-document-path-${card.value}`}
              className={cn(
                "text-left rounded-md border p-space-3 transition-colors duration-fast ease-standard",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-1",
                selected
                  ? "border-gold bg-gold-tint shadow-[inset_0_0_0_1px_var(--color-gold)]"
                  : "border-line bg-white-pure hover:border-gold hover:bg-gold-tint",
              )}
            >
              {hasText(label) && (
                <span className="block text-small font-semibold text-ink">{label}</span>
              )}
              {hasText(body) && <span className="block text-small text-ink-mid mt-space-1">{body}</span>}
              {/* The line that earns the card: what Phoenix hands back. */}
              {hasText(produces) && (
                <span className="block text-caption text-ink-faint mt-space-2">{produces}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The review step’s read-only recap of the deal the buyer named.
 *
 * READ-ONLY on purpose: the way to change a figure is the stepper or Back, and
 * a second set of inputs for the same answers is a second place they can
 * disagree. Rendered only on the two arms that named a deal — a letter-only
 * buyer has no deal to recap, and an empty card headed "The deal you told us
 * about" would be asking them what they got wrong.
 *
 * Every row is omitted when its answer is blank rather than rendered as a dash:
 * three of these are optional, and a recap is a list of what the buyer SAID.
 */
function DealRecap({
  businessName,
  industry,
  price,
  cashFlow,
  closeDate,
  internal,
}: {
  businessName: string;
  industry: string;
  price: string;
  cashFlow: string;
  closeDate: string;
  internal: boolean;
}) {
  const rows: { label: string; value: string }[] = [];
  const push = (label: string, raw: string) => {
    const trimmed = raw.trim();
    if (trimmed !== "") rows.push({ label, value: trimmed });
  };
  push("Business", businessName);
  push("Industry", industry);
  const priceAmount = parseMoney(price);
  if (priceAmount !== null) rows.push({ label: "Asking price", value: formatCurrency(priceAmount) });
  const cashFlowAmount = parseMoney(cashFlow);
  if (cashFlowAmount !== null) {
    rows.push({ label: "Annual free cash flow", value: formatCurrency(cashFlowAmount) });
  }
  push("Expected close", closeDate);

  return (
    <section
      className="border border-line rounded-md p-space-3 bg-white-pure"
      data-testid="prequal-review-deal-recap"
    >
      <p className="text-small font-semibold text-ink mt-0 mb-space-2">
        {REVIEW_DEAL_RECAP_TITLE}
      </p>
      <dl className="grid grid-cols-1 sm:grid-cols-[auto_minmax(0,1fr)] gap-x-space-3 gap-y-space-1 m-0">
        {rows.map((row) => (
          <Fragment key={row.label}>
            <dt className="text-caption text-ink-faint m-0">{row.label}</dt>
            <dd className="text-small text-ink m-0">{row.value}</dd>
          </Fragment>
        ))}
      </dl>
      {internal && (
        <p className="text-caption text-ink-mid mt-space-3 mb-0">
          {REVIEW_DEAL_RECAP_NOTE_INTERNAL}
        </p>
      )}
    </section>
  );
}

/**
 * What the funnel now SAYS instead of asking "cash at closing (optional)".
 *
 * 🔴 THE ASSUMPTION WAS ALWAYS BEING MADE; ONLY THE FIELD IS GONE. Phoenix
 * requires no down payment and sets no minimum, so a box asking a buyer to
 * nominate one invited them to guess at a requirement that does not exist — and
 * sitting empty beneath two required figures, it read as something they had
 * failed to fill in. The capital step already asks what they have. Stating the
 * assumption in their own figure is the difference between a rule they can
 * check and one they have to infer.
 *
 * A buyer who named no capital gets the same sentence without the number.
 * "We’ll assume the $0 you told us about" is not a sentence to show anybody.
 *
 * 🔴 THE FIGURE IS {@link statedCashAtClosing}, NEVER
 * {@link totalDownPaymentCapacity}. This sentence says the money goes to the
 * SELLER AT CLOSING, so it must name the same quantity the Deal Sheet's
 * cash-at-closing row names — and capacity is a different quantity, summing
 * pledgeable non-cash assets at their haircuts. It shipped reading capacity:
 * a buyer with $50,000 of cash and $200,000 of retirement accounts was told
 * $150,000 "goes to the seller at closing" one screen before submit, and then
 * received a sheet printing $50,000. Two numbers, one question, minutes apart.
 *
 * ⚠️ THE PARAGRAPH THAT STOOD HERE ANSWERED THE WRONG OBJECTION, which is why
 * the figure survived review. It said — correctly — that the sentence is about
 * the transaction and not about the sizing, and that the internal arm counts
 * none of this toward what the buyer qualifies for. Every clause of that was
 * true. It simply says nothing about whether the NUMBER is the right number,
 * and a reader who found a considered-looking note at exactly the place they
 * were about to ask stopped asking. Both claims are kept below, because they
 * are still the two ways to get this component wrong.
 *
 * ⚠️ IT IS A STATEMENT ABOUT THE TRANSACTION, NOT ABOUT THE SIZING, and on the
 * internal arm the difference matters: that arm sizes from the business’s cash
 * flow and reads a down payment of ZERO (the funnel no longer states one), so
 * nothing here is being counted toward what the buyer qualifies for. The recap
 * directly above says so in as many words on that arm. Do not reword this into
 * a claim that the figure was applied — and do not widen the figure to capacity
 * to make it look like more, which is the same mistake pointing the other way.
 */
function CashAtClosingAssumption({ cash }: { cash: number }) {
  return (
    <p className="text-small text-ink-mid m-0" data-testid="prequal-review-cash-assumption">
      {cash > 0 ? (
        <>
          {REVIEW_CASH_ASSUMPTION_PREFIX}
          <strong className="text-ink">{formatCurrency(cash)}</strong>
          {REVIEW_CASH_ASSUMPTION_SUFFIX}
        </>
      ) : (
        REVIEW_CASH_ASSUMPTION_NONE
      )}
    </p>
  );
}

/**
 * "Other assets toward your down payment" — the optional, self-reported
 * non-cash half of the capital step.
 *
 * Ticking a category REVEALS its dollar input, and only a ticked category
 * carrying a figure is ever submitted: an untouched one is absent from the
 * payload rather than sent as 0, so a cash-only application looks exactly as
 * it did before this section existed and no document grows an empty breakdown.
 *
 * ONE shared note covers everything ticked. A note per category would be five
 * mostly-empty boxes here and five mostly-empty rows on three documents — and
 * the buyer explaining what "Other" is has one thing to say, not five.
 *
 * Nothing here is verified at prequalification, and the copy says so rather
 * than implying a figure has been checked.
 */
function OtherAssetsSection({
  read,
  selected,
  amounts,
  note,
  onToggle,
  onAmountChange,
  onNoteChange,
}: {
  /** The heading, the five category labels and the note's label and helper are
   *  Document Language blocks (D7); the intro below the heading is not. */
  read: QuestionCopy;
  selected: Partial<Record<OtherAssetKey, boolean>>;
  amounts: Partial<Record<OtherAssetKey, string>>;
  note: string;
  onToggle: (key: OtherAssetKey, checked: boolean) => void;
  onAmountChange: (key: OtherAssetKey, raw: string) => void;
  onNoteChange: (value: string) => void;
}) {
  // The checkboxes are a group; each one names itself from its wrapping
  // `<label>`, and the group takes its name from the visible heading rather
  // than from a `<label for>` aimed at a `<div>` (which names nothing).
  const headingId = useId();
  const anySelected = OTHER_ASSET_CATEGORIES.some((key) => selected[key]);
  const heading = read("heading-buyer-other-assets");

  return (
    <div className="border border-line rounded-md p-space-3 bg-white-pure">
      {hasText(heading) && (
        <p id={headingId} className="text-small text-ink mt-0 mb-space-1">
          {heading}
        </p>
      )}
      <p className="text-caption text-ink-mid mt-0 mb-space-3">
        Optional. Tick anything you plan to put toward the down payment alongside your cash, and
        tell us roughly what it’s worth. These figures are self-reported — nothing here is
        verified at this stage.
      </p>

      <div
        role="group"
        aria-labelledby={hasText(heading) ? headingId : undefined}
        className="flex flex-col gap-space-3"
      >
        {OTHER_ASSET_CATEGORIES.map((key) => {
          const label = read(otherAssetBlock(key));
          return (
            <div key={key}>
              <label className="flex items-start gap-space-2 text-small text-ink cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-space-1 accent-gold"
                  checked={selected[key] ?? false}
                  onChange={(e) => onToggle(key, e.target.checked)}
                  data-testid={`prequal-other-asset-${key}`}
                />
                {hasText(label) && <span>{label}</span>}
              </label>
              {selected[key] && (
                <div className="mt-space-2 pl-space-3 border-l-2 border-gold-soft">
                  {/* Built from the RENDERED label; a blank one leaves the
                      amount unlabelled rather than reading "— amount". */}
                  <CurrencyField
                    label={hasText(label) ? `${label} — amount` : ""}
                    required
                    value={amounts[key] ?? ""}
                    onChange={(raw) => onAmountChange(key, raw)}
                    testId={`prequal-other-asset-${key}-amount`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {anySelected && (
        <div className="mt-space-3">
          <FormField
            label={textOrUndefined(read("label-buyer-other-assets-note"))}
            helperText={textOrUndefined(read("helper-buyer-other-assets-note"))}
          >
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              data-testid="prequal-other-assets-note"
            />
          </FormField>
        </div>
      )}
    </div>
  );
}

/**
 * What the funnel root shows when this browser is holding a COMPLETED
 * application for this exact context.
 *
 * Three named ways out, because three genuinely different people arrive here:
 * the applicant coming back for their documents, the applicant who spotted a
 * mistake, and the broker whose next client just sat down. Guessing between
 * them either hides somebody's results or shows a stranger's — so it asks.
 *
 * Nothing here destroys anything on its own: only the explicitly-labelled
 * "Start a new application" clears the stored answers, and the Result route
 * keeps reading them until it does.
 *
 * DUPLICATED VERBATIM in SellerPrequalifyPage. The two funnel pages are
 * deliberate mirrors and their one shared home, PrequalBits, is owned
 * elsewhere in this change — keep the two copies in lockstep.
 */
function CompletedApplicationCard({
  role,
  finalized,
  onView,
  onAdjust,
  onStartNew,
}: {
  role: "Buyer" | "Seller";
  finalized: PrequalFinalizeResult;
  onView: () => void;
  onAdjust: () => void;
  onStartNew: () => void;
}) {
  const reference = finalized.referenceNumber?.trim();
  // A soft decline's result route shows the warm thank-you rather than a
  // documents view, so the button must not promise one. (The premise used to be
  // that a decline HAS no documents. It has two now — they are emailed, and the
  // result screen says so — but there is still nothing here to go and look at.)
  const viewLabel =
    finalized.result?.outcome === "SoftDecline" ? "View my result" : "View my documents";

  return (
    <section
      className="rounded-[14px] border border-line bg-white-pure shadow-[0_10px_34px_rgba(20,20,20,0.07)] p-space-4 sm:p-space-6 max-w-2xl mx-auto"
      data-testid="prequal-completed-gate"
      aria-live="polite"
    >
      <p className="text-label uppercase tracking-widest sm:tracking-[0.24em] text-gold-deep mt-0 mb-space-2">
        Already submitted
      </p>
      <h1 className="font-semibold normal-case tracking-normal text-[1.45rem] leading-tight text-ink mt-0 mb-space-3">
        You’ve already finished this application
      </h1>
      <p className="text-small text-ink-mid m-0">
        This browser is still holding a completed{" "}
        {role === "Buyer" ? "buyer" : "seller"} prequalification
        {reference ? (
          <>
            , reference <strong className="text-ink">{reference}</strong>
          </>
        ) : null}
        . Nothing has been lost — choose what you’d like to do.
      </p>

      <div className="flex flex-wrap items-center gap-space-3 mt-space-4">
        <Button type="button" onClick={onView} data-testid="prequal-completed-view">
          {viewLabel}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onStartNew}
          data-testid="prequal-completed-new"
        >
          Start a new application
        </Button>
      </div>
      <p className="text-caption text-ink-faint mt-space-2 mb-0">
        Starting a new application clears these answers from this browser. If somebody else is
        applying, that’s the one to choose.
      </p>

      <p className="text-small text-ink-mid mt-space-4 mb-0">
        Or{" "}
        <button
          type="button"
          onClick={onAdjust}
          data-testid="prequal-completed-adjust"
          className="bg-transparent border-0 p-0 text-gold-deep underline cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-sm"
        >
          adjust my answers
        </button>
        {" "}— reopen this application and change it.
      </p>
    </section>
  );
}

/** The client half of the server's company check (PrequalCompanyBuyerAnswers):
 *  same required fields, same refusal of $ and % in the name, same words. The
 *  server repeats it, because a resumed or assisted draft can skip this. */
export function validateCompanyDetails(
  form: Pick<BuyerForm, "acquiringCompanyName" | "acquiringCompanyTitle">,
): string | null {
  const name = form.acquiringCompanyName.trim();
  if (!name) return "Tell us the name of the acquiring company.";
  if (!form.acquiringCompanyTitle.trim()) return "Tell us your title at the acquiring company.";
  if (/[$%]/.test(name)) return "The acquiring company name cannot contain $ or %.";
  return null;
}

/**
 * The acquiring company's details (D2), rendered inside FinalizeSection beside
 * the person's name — after Phone, before the honeypot.
 *
 * 🔴 THE WEBSITE INPUT IS NOT NAMED `website`. That name belongs to the hidden
 * honeypot in the same form, and a browser or extension autofilling a visible
 * "website" field could fill the honeypot too and reject a real applicant. It
 * is `acquiringCompanyWebsite`, with `autoComplete="url"`, and its value rides
 * inside `buyer` on the wire — never the top-level `website`.
 */
function CompanyDetailsFields({
  form,
  marketing,
  onChange,
}: {
  form: BuyerForm;
  marketing: PrequalFunnelMarketingCopy;
  onChange: (patch: Partial<BuyerForm>) => void;
}) {
  return (
    <div className="flex flex-col gap-space-3" data-testid="prequal-company-details">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
        <FormField label={textOrUndefined(marketing.labelBuyerCompanyName)} required>
          <Input
            name="acquiringCompanyName"
            autoComplete="organization"
            maxLength={COMPANY_FIELD_LIMITS.companyName}
            value={form.acquiringCompanyName}
            onChange={(e) => onChange({ acquiringCompanyName: e.target.value })}
            data-testid="prequal-company-name"
          />
        </FormField>
        <FormField label={textOrUndefined(marketing.labelBuyerCompanyTitle)} required>
          <Input
            name="acquiringCompanyTitle"
            autoComplete="organization-title"
            maxLength={COMPANY_FIELD_LIMITS.title}
            value={form.acquiringCompanyTitle}
            onChange={(e) => onChange({ acquiringCompanyTitle: e.target.value })}
            data-testid="prequal-company-title"
          />
        </FormField>
      </div>
      <FormField label={textOrUndefined(marketing.labelBuyerCompanyWebsite)}>
        <Input
          type="url"
          name="acquiringCompanyWebsite"
          autoComplete="url"
          inputMode="url"
          maxLength={COMPANY_FIELD_LIMITS.website}
          value={form.acquiringCompanyWebsite}
          onChange={(e) => onChange({ acquiringCompanyWebsite: e.target.value })}
          data-testid="prequal-company-website"
        />
      </FormField>
      <FormField
        label={textOrUndefined(marketing.labelBuyerCompanyAbout)}
        helperText={textOrUndefined(marketing.helperBuyerCompanyAbout)}
      >
        <Textarea
          rows={3}
          name="acquiringCompanyAbout"
          maxLength={COMPANY_FIELD_LIMITS.about}
          value={form.acquiringCompanyAbout}
          onChange={(e) => onChange({ acquiringCompanyAbout: e.target.value })}
          data-testid="prequal-company-about"
        />
      </FormField>
    </div>
  );
}
